import React, { useState, useEffect, useRef, useCallback } from 'react';
import html2canvas from 'html2canvas';
import {
    BuildingType, ResourceState, Building, GameEvent, GameEventOption,
    LogEntry, Era, Season, EventChain, Character, CharacterId, EndingType,
    MiningUpgrade, MiningUpgradeType, MiniGameType, HiddenPictureGame, PuzzleGame
} from './types';
import {
    INITIAL_BUILDINGS, INITIAL_CHARACTERS, TICK_RATE_MS,
    EVENT_CHANCE_PER_TICK, SEASON_LENGTH, TIPS,
    INITIAL_MINING_UPGRADES, MINIGAME_TRIGGER_THRESHOLD, MINIGAME_TRIGGER_WINDOW,
    MINIGAME_MIN_CLICKS, HIDDEN_PICTURE_EMOJIS, PUZZLE_EMOJIS, MINIGAME_REWARDS
} from './constants';
import {
    filterAvailableEvents, selectRandomEvent, processEventOption,
    EventContext, startChain, advanceChain, calculateEra, advanceSeason,
    getEraName, getSeasonName, checkEndings
} from './services/eventSystem';
import { ALL_EVENTS } from './services/eventData';
import {
    DuckMBTI, INITIAL_MBTI, updateMBTI, calculateMBTIType,
    DUCK_PERSONALITIES
} from './services/duckMBTI';
import { ENDINGS, generateEndingScreen } from './services/endingSystem';
import { fetchGlobalRankings, submitGlobalRanking, GlobalRankingEntry } from './services/rankingApi';
import BuildingPanel from './components/BuildingPanel';
import EventModal from './components/EventModal';

// ============================================
// 난이도 시스템
// ============================================
type GameDifficulty = 'easy' | 'hard';

interface DifficultySettings {
    name: string;
    icon: string;
    description: string;
    clickFatigueThreshold: number;  // 클릭 과로 기준
    choiceFatigueCount: number;     // 연속 선택 피로도 기준
    generationDecayRate: number;    // 세대 불만족 감소율
    rumorChance: number;            // 소문 발생 확률
    rumorDistortChance: number;     // 소문 왜곡 확률
    eventPenaltyMultiplier: number; // 이벤트 페널티 배율
    incomeMultiplier: number;       // 수입 배율
}

const DIFFICULTY_SETTINGS: Record<GameDifficulty, DifficultySettings> = {
    easy: {
        name: '이지 모드',
        icon: '🌸',
        description: '느긋하게 왕국을 즐기세요!',
        clickFatigueThreshold: 15,   // 15클릭까지 허용
        choiceFatigueCount: 4,       // 4연속까지 허용
        generationDecayRate: 0.05,   // 세대 불만족 느리게
        rumorChance: 0.15,           // 소문 15%
        rumorDistortChance: 0.3,     // 왜곡 30%
        eventPenaltyMultiplier: 0.5, // 페널티 50%
        incomeMultiplier: 1.5        // 수입 150%
    },
    hard: {
        name: '하드 모드',
        icon: '🔥',
        description: '진정한 왕의 시련!',
        clickFatigueThreshold: 8,    // 8클릭만 허용
        choiceFatigueCount: 2,       // 2연속도 위험
        generationDecayRate: 0.15,   // 세대 불만족 빠르게
        rumorChance: 0.4,            // 소문 40%
        rumorDistortChance: 0.6,     // 왜곡 60%
        eventPenaltyMultiplier: 1.5, // 페널티 150%
        incomeMultiplier: 0.8        // 수입 80%
    }
};

// ============================================
// 랭킹 시스템
// ============================================
interface RankingEntry {
    name: string;
    score: number;
    money: number;
    happiness: number;
    population: number;
    ending: string;
    mbti: string;
    date: string;
    difficulty: GameDifficulty;
}

const RANKING_KEY_EASY = 'oring_kingdom_rankings_easy';
const RANKING_KEY_HARD = 'oring_kingdom_rankings_hard';
const SAVE_KEY_PREFIX = 'oring_kingdom_save_';

// ============================================
// 저장 시스템
// ============================================
interface SaveData {
    playerName: string;
    password?: string;
    difficulty: GameDifficulty;
    resources: ResourceState;
    buildings: Record<BuildingType, Building>;
    characters: Record<CharacterId, Character>;
    activeChains: EventChain[];
    completedChains: string[];
    flags: string[];
    triggeredEvents: string[];
    mbti: DuckMBTI;
    miningUpgrades: Record<MiningUpgradeType, MiningUpgrade>;
    stats: {
        totalClicks: number;
        totalMoneyEarned: number;
        totalMoneySpent: number;
        eventsTriggered: number;
        chainsCompleted: number;
        playTimeSeconds: number;
        highestPopulation: number;
        highestMoney: number;
    };
    elderSatisfaction: number;
    youthSatisfaction: number;
    goodRumors: number;
    badRumors: number;
    savedAt: string;
}

const saveGame = (name: string, data: SaveData): void => {
    try {
        const key = SAVE_KEY_PREFIX + name.toLowerCase();
        localStorage.setItem(key, JSON.stringify(data));
        console.log(`[저장 완료] 키: ${key}, 턴: ${data.resources.turn}`);
    } catch (e) {
        console.error('Save failed:', e);
    }
};

const loadGame = (name: string): SaveData | null => {
    try {
        const data = localStorage.getItem(SAVE_KEY_PREFIX + name.toLowerCase());
        return data ? JSON.parse(data) : null;
    } catch {
        return null;
    }
};

const hasSaveData = (name: string): boolean => {
    const key = SAVE_KEY_PREFIX + name.toLowerCase();
    const exists = localStorage.getItem(key) !== null;
    console.log(`[저장 확인] 키: ${key}, 존재: ${exists}`);
    return exists;
};

const hasPassword = (name: string): boolean => {
    const saveData = loadGame(name);
    return saveData?.password ? true : false;
};

const deleteSaveData = (name: string): void => {
    localStorage.removeItem(SAVE_KEY_PREFIX + name.toLowerCase());
};

// ============================================
// 시대 업그레이드 조건
// ============================================
const ERA_REQUIREMENTS: Record<Era, { population: number; money: number } | null> = {
    [Era.POND]: null,
    [Era.VILLAGE]: { population: 50, money: 1000 },
    [Era.CITY]: { population: 200, money: 10000 },
    [Era.KINGDOM]: { population: 500, money: 100000 },
    [Era.EMPIRE]: { population: 1000, money: 1000000 }
};

const getNextEra = (currentEra: Era): Era | null => {
    const order: Era[] = [Era.POND, Era.VILLAGE, Era.CITY, Era.KINGDOM, Era.EMPIRE];
    const currentIndex = order.indexOf(currentEra);
    return currentIndex < order.length - 1 ? order[currentIndex + 1] : null;
};

// ============================================
// 엔딩 이미지 매핑
// ============================================
const ENDING_IMAGES: Record<EndingType, string[]> = {
    [EndingType.CONQUEST]: ['milestone_1000_pop', 'crisis_fox_army', 'chain_fox_battle'],
    [EndingType.PEACE]: ['daily_sunny_day', 'spring_cherry', 'opportunity_rainbow'],
    [EndingType.GOLDEN_DUCK]: ['opportunity_golden_egg', 'milestone_millionaire', 'daily_talent_show'],
    [EndingType.SKY_KINGDOM]: ['chain_sky_migration', 'chain_sky_training', 'spring_migration'],
    [EndingType.SEA_KINGDOM]: ['chain_sea_island', 'chain_sea_storm', 'opportunity_pirate'],
    [EndingType.LEGEND]: ['chain_true_complete', 'char_sage_advice', 'milestone_happiness'],
    [EndingType.MERCHANT_EMPIRE]: ['opportunity_merchant', 'daily_market_day', 'daily_foreign_goods'],
    [EndingType.MIGRATION]: ['spring_migration', 'autumn_goodbye', 'chain_sky_proposal'],
    [EndingType.FOX_ALLIANCE]: ['chain_fox_peace', 'chain_fox_negotiate', 'char_fox_meeting'],
    [EndingType.ASCENSION]: ['chain_true_enlighten', 'opportunity_swan', 'chain_true_prophecy'],
    [EndingType.EXTINCTION]: ['crisis_famine', 'winter_frozen', 'crisis_plague'],
    [EndingType.REBELLION]: ['crisis_rebellion', 'daily_corruption', 'crisis_fire'],
    [EndingType.FOX_CONQUEST]: ['chain_fox_war', 'crisis_fox_army', 'chain_fox_final'],
    [EndingType.STARVATION]: ['crisis_famine', 'winter_sick', 'daily_worm_shortage'],
    [EndingType.PLAGUE]: ['crisis_plague', 'winter_sick', 'crisis_flood'],
    [EndingType.TRUE_ENDING]: ['chain_true_complete', 'chain_true_enlighten', 'chain_true_prophecy'],
    [EndingType.SECRET_DUCK]: ['opportunity_golden_egg', 'opportunity_turtle', 'char_crow_secret'],
    [EndingType.TIME_LOOP]: ['autumn_moon', 'winter_newyear', 'chain_true_prophecy']
};

const getRandomEndingImage = (endingType: EndingType): string => {
    const images = ENDING_IMAGES[endingType] || ['daily_sunny_day'];
    return `/assets/events/${images[Math.floor(Math.random() * images.length)]}.webp`;
};

const loadRankings = (difficulty: GameDifficulty): RankingEntry[] => {
    try {
        const key = difficulty === 'easy' ? RANKING_KEY_EASY : RANKING_KEY_HARD;
        const data = localStorage.getItem(key);
        const rankings = data ? JSON.parse(data) : [];
        console.log(`[랭킹 불러오기] 키: ${key}, 개수: ${rankings.length}`);
        return rankings;
    } catch (e) {
        console.error('[랭킹 불러오기 실패]', e);
        return [];
    }
};

const saveRanking = (entry: RankingEntry) => {
    try {
        const key = entry.difficulty === 'easy' ? RANKING_KEY_EASY : RANKING_KEY_HARD;
        const rankings = loadRankings(entry.difficulty);
        rankings.push(entry);
        rankings.sort((a, b) => b.score - a.score);
        const newRankings = rankings.slice(0, 20);
        localStorage.setItem(key, JSON.stringify(newRankings));
        console.log(`[랭킹 저장] 키: ${key}, 이름: ${entry.name}, 점수: ${entry.score}, 총 ${newRankings.length}개`);
    } catch (e) {
        console.error('[랭킹 저장 실패]', e);
    }
};

const calculateScore = (money: number, happiness: number, population: number, isPositiveEnding: boolean): number => {
    const moneyScore = Math.floor(money / 100);
    const happinessScore = happiness * 100;
    const populationScore = population * 50;
    const endingBonus = isPositiveEnding ? 5000 : 0;
    return moneyScore + happinessScore + populationScore + endingBonus;
};

// ============================================
// MBTI 표시 헬퍼
// ============================================
const getMBTIDisplay = (mbti: DuckMBTI): string => {
    const threshold = 5; // 낮은 threshold로 빠르게 변화 표시
    const e = Math.abs(mbti.extroversion) >= threshold ? (mbti.extroversion > 0 ? 'E' : 'I') : '?';
    const s = Math.abs(mbti.sensing) >= threshold ? (mbti.sensing > 0 ? 'S' : 'N') : '?';
    const t = Math.abs(mbti.thinking) >= threshold ? (mbti.thinking > 0 ? 'T' : 'F') : '?';
    const j = Math.abs(mbti.judging) >= threshold ? (mbti.judging > 0 ? 'J' : 'P') : '?';
    return `${e}${s}${t}${j}`;
};

// ============================================
// 선택 유형 분류 (기믹용)
// ============================================
type ChoiceType = 'saving' | 'spending' | 'peaceful' | 'aggressive' | 'traditional' | 'innovative' | 'neutral';

const classifyChoice = (option: GameEventOption): ChoiceType => {
    const text = option.text.toLowerCase();

    // 절약 vs 지출
    if (option.moneyChange > 50 || text.includes('절약') || text.includes('저장') || text.includes('아끼')) return 'saving';
    if (option.moneyChange < -100 || text.includes('파티') || text.includes('축제') || text.includes('잔치') || text.includes('투자')) return 'spending';

    // 평화 vs 공격
    if (text.includes('평화') || text.includes('협상') || text.includes('대화') || text.includes('용서')) return 'peaceful';
    if (text.includes('전쟁') || text.includes('공격') || text.includes('싸') || text.includes('진압')) return 'aggressive';

    // 전통 vs 혁신
    if (text.includes('전통') || text.includes('원로') || text.includes('조상') || text.includes('지키')) return 'traditional';
    if (text.includes('혁신') || text.includes('새로') || text.includes('변화') || text.includes('개혁')) return 'innovative';

    return 'neutral';
};

// ============================================
// 소문 메시지
// ============================================
const GOOD_RUMORS = [
    "왕이 백성을 위해 자신의 식사를 나눴대!",
    "왕이 아픈 오리를 직접 간호했대!",
    "왕이 비밀리에 가난한 오리들을 도왔대!",
    "왕이 여우와 맨손으로 싸워 이겼대!",
    "왕에게 축복의 빛이 내렸대!"
];

const BAD_RUMORS = [
    "왕이 뇌물을 받았대!",
    "왕이 백성의 먹이를 빼돌렸대!",
    "왕이 여우와 내통하고 있대!",
    "왕이 사실 가짜 오리래!",
    "왕궁에서 밤마다 이상한 소리가 난대!"
];

// ============================================
// 게임 화면 타입
// ============================================
type GameScreen = 'title' | 'game' | 'ending';

const App: React.FC = () => {
    // --- Screen State ---
    const [screen, setScreen] = useState<GameScreen>('title');
    const [playerName, setPlayerName] = useState('');
    const [nameInput, setNameInput] = useState('');
    const [passwordInput, setPasswordInput] = useState('');
    const [savedPassword, setSavedPassword] = useState('');
    const [showSaveSuccess, setShowSaveSuccess] = useState(false);
    const [showRanking, setShowRanking] = useState(false);
    const [rankingsEasy, setRankingsEasy] = useState<RankingEntry[]>([]);
    const [rankingsHard, setRankingsHard] = useState<RankingEntry[]>([]);
    const [globalRankingsEasy, setGlobalRankingsEasy] = useState<GlobalRankingEntry[]>([]);
    const [globalRankingsHard, setGlobalRankingsHard] = useState<GlobalRankingEntry[]>([]);
    const [rankingTab, setRankingTab] = useState<GameDifficulty>('easy');
    const [rankingMode, setRankingMode] = useState<'local' | 'global'>('local');
    const [globalRank, setGlobalRank] = useState<number | null>(null);
    const [difficulty, setDifficulty] = useState<GameDifficulty>('easy');
    const [hasSave, setHasSave] = useState(false);
    const [showLoadConfirm, setShowLoadConfirm] = useState(false);
    const [endingImage, setEndingImage] = useState<string>('');
    const [showHelp, setShowHelp] = useState(false);
    const [helpTab, setHelpTab] = useState<'basic' | 'ui' | 'endings' | 'buildings'>('basic');

    // --- Core State ---
    const [resources, setResources] = useState<ResourceState>({
        money: 0,
        population: 5,
        happiness: 50,
        turn: 1,
        era: Era.POND,
        season: Season.SPRING,
        day: 1
    });
    const [buildings, setBuildings] = useState<Record<BuildingType, Building>>(INITIAL_BUILDINGS);
    const [characters, setCharacters] = useState<Record<CharacterId, Character>>(INITIAL_CHARACTERS);

    // --- Event State ---
    const [currentEvent, setCurrentEvent] = useState<GameEvent | null>(null);
    const [activeChains, setActiveChains] = useState<EventChain[]>([]);
    const [completedChains, setCompletedChains] = useState<string[]>([]);
    const [flags, setFlags] = useState<Set<string>>(new Set());
    const [triggeredEvents, setTriggeredEvents] = useState<Set<string>>(new Set());

    // --- MBTI State ---
    const [mbti, setMbti] = useState<DuckMBTI>(INITIAL_MBTI);

    // --- UI State ---
    const [logs, setLogs] = useState<LogEntry[]>([]);
    const [isClicking, setIsClicking] = useState(false);
    const [currentTip, setCurrentTip] = useState(0);

    // --- Ending State ---
    const [currentEnding, setCurrentEnding] = useState<EndingType | null>(null);
    const [isGameOver, setIsGameOver] = useState(false);

    // ============================================
    // 🎮 기믹 1: 연속 선택 피로도
    // ============================================
    const [recentChoices, setRecentChoices] = useState<ChoiceType[]>([]);
    const [choiceFatigue, setChoiceFatigue] = useState(0);

    // ============================================
    // 🎮 기믹 2: 클릭 과로 시스템
    // ============================================
    const [clickTimestamps, setClickTimestamps] = useState<number[]>([]);
    const [isOverworked, setIsOverworked] = useState(false);
    const [overworkTimer, setOverworkTimer] = useState(0);
    const [duckFace, setDuckFace] = useState('🦆');

    // ============================================
    // 🎮 기믹 3: 세대 갈등
    // ============================================
    const [elderSatisfaction, setElderSatisfaction] = useState(50); // 0-100
    const [youthSatisfaction, setYouthSatisfaction] = useState(50); // 0-100

    // ============================================
    // 🎮 기믹 4: 소문 시스템
    // ============================================
    const [goodRumors, setGoodRumors] = useState(0);
    const [badRumors, setBadRumors] = useState(0);
    const [lastRumorMessage, setLastRumorMessage] = useState('');

    // --- Statistics ---
    const [stats, setStats] = useState({
        totalClicks: 0,
        totalMoneyEarned: 0,
        totalMoneySpent: 0,
        eventsTriggered: 0,
        chainsCompleted: 0,
        playTimeSeconds: 0,
        highestPopulation: 5,
        highestMoney: 0
    });

    // ============================================
    // ⛏️ 채굴 업그레이드 시스템
    // ============================================
    const [miningUpgrades, setMiningUpgrades] = useState<Record<MiningUpgradeType, MiningUpgrade>>(
        JSON.parse(JSON.stringify(INITIAL_MINING_UPGRADES))
    );

    // 채굴 속도 추적 (미니게임 트리거용)
    const [miningHistory, setMiningHistory] = useState<{ time: number; amount: number }[]>([]);

    // ============================================
    // 🎮 미니게임 시스템
    // ============================================
    const [activeMiniGame, setActiveMiniGame] = useState<MiniGameType | null>(null);
    const [miniGameData, setMiniGameData] = useState<HiddenPictureGame | PuzzleGame | null>(null);
    const [miniGameTimer, setMiniGameTimer] = useState(0);
    const [miniGameScore, setMiniGameScore] = useState(0);

    // ============================================
    // 🎉 시각적 보상 시스템
    // ============================================
    const [celebration, setCelebration] = useState<{
        type: 'money' | 'era' | 'population';
        message: string;
        icon: string;
    } | null>(null);
    const [lastClickTime, setLastClickTime] = useState(0);
    const [comboCount, setComboCount] = useState(0);
    const [showCombo, setShowCombo] = useState(false);

    // 모바일 팝업 상태
    const [showMiningPopup, setShowMiningPopup] = useState(false);
    const [showBuildingPopup, setShowBuildingPopup] = useState(false);

    // --- Refs ---
    const clickAreaRef = useRef<HTMLDivElement>(null);
    const startTimeRef = useRef<number>(Date.now());
    const lastMilestones = useRef({ money: 0, population: 0 });
    const endingCardRef = useRef<HTMLDivElement>(null);

    // 랭킹 로드 (로컬)
    useEffect(() => {
        setRankingsEasy(loadRankings('easy'));
        setRankingsHard(loadRankings('hard'));
    }, []);

    // 글로벌 랭킹 로드
    useEffect(() => {
        const loadGlobalRankings = async () => {
            const [easy, hard] = await Promise.all([
                fetchGlobalRankings('easy', 20),
                fetchGlobalRankings('hard', 20)
            ]);
            setGlobalRankingsEasy(easy);
            setGlobalRankingsHard(hard);
        };
        loadGlobalRankings();
    }, []);

    // 현재 난이도 설정
    const diffSettings = DIFFICULTY_SETTINGS[difficulty];

    // --- Helpers ---
    const addLog = (message: string, type: LogEntry['type'] = 'info', icon?: string) => {
        setLogs(prev => [{
            id: Date.now() + Math.random(),
            message,
            type,
            icon
        }, ...prev].slice(0, 100));
    };

    const calculateIncome = useCallback(() => {
        let income = 0;
        Object.values(buildings).forEach((b: Building) => {
            income += b.incomePerTick * b.count;
        });

        const seasonMultiplier = resources.season === Season.AUTUMN ? 1.3 :
            resources.season === Season.SUMMER ? 1.2 :
                resources.season === Season.WINTER ? 0.7 : 1.0;

        const happinessMultiplier = resources.happiness / 50;
        const mbtiType = calculateMBTIType(mbti);
        const mbtiMultiplier = DUCK_PERSONALITIES[mbtiType].bonusEffects.moneyMultiplier;

        // 🎮 기믹 2: 과로 시 수입 50% 감소
        const overworkMultiplier = isOverworked ? 0.5 : 1.0;

        // 난이도 배율
        const difficultyMultiplier = DIFFICULTY_SETTINGS[difficulty].incomeMultiplier;

        return Math.floor(income * seasonMultiplier * happinessMultiplier * mbtiMultiplier * overworkMultiplier * difficultyMultiplier);
    }, [buildings, resources.happiness, resources.season, mbti, isOverworked, difficulty]);

    const getTotalDefense = useCallback(() => {
        return (Object.values(buildings) as Building[]).reduce((sum, b) => sum + (b.defenseBonus * b.count), 0);
    }, [buildings]);

    // ============================================
    // ⛏️ 채굴 값 계산
    // ============================================
    const calculateClickValue = useCallback(() => {
        let baseValue = 1;
        let multiplier = 1;
        let luckyChance = 0;

        (Object.values(miningUpgrades) as MiningUpgrade[]).forEach(upgrade => {
            if (upgrade.level > 0 && upgrade.effect) {
                if (upgrade.effect.clickBonus) {
                    baseValue += upgrade.effect.clickBonus * upgrade.level;
                }
                if (upgrade.effect.clickMultiplier) {
                    multiplier *= Math.pow(upgrade.effect.clickMultiplier, upgrade.level);
                }
                if (upgrade.effect.luckyChance) {
                    luckyChance += upgrade.effect.luckyChance * upgrade.level;
                }
            }
        });

        // 럭키 체크
        const isLucky = Math.random() < Math.min(luckyChance, 0.5); // 최대 50%
        if (isLucky) multiplier *= 2;

        return { value: Math.floor(baseValue * multiplier), isLucky };
    }, [miningUpgrades]);

    const calculateAutoMining = useCallback(() => {
        let autoValue = 0;
        (Object.values(miningUpgrades) as MiningUpgrade[]).forEach(upgrade => {
            if (upgrade.level > 0 && upgrade.effect?.autoMining) {
                autoValue += upgrade.effect.autoMining * upgrade.level;
            }
        });
        return autoValue;
    }, [miningUpgrades]);

    const getUpgradeCost = useCallback((type: MiningUpgradeType) => {
        const upgrade = miningUpgrades[type];
        return Math.floor(upgrade.baseCost * Math.pow(upgrade.costMultiplier, upgrade.level));
    }, [miningUpgrades]);

    // ============================================
    // 🎮 미니게임 헬퍼
    // ============================================
    const checkMiniGameTrigger = useCallback(() => {
        const now = Date.now();
        const recentMining = miningHistory.filter(h => now - h.time < MINIGAME_TRIGGER_WINDOW);
        const totalMined = recentMining.reduce((sum, h) => sum + h.amount, 0);
        const clickCount = recentMining.length;

        // 최소 클릭 횟수 + 오링 합계 복합 조건
        if (clickCount >= MINIGAME_MIN_CLICKS && totalMined >= MINIGAME_TRIGGER_THRESHOLD && !activeMiniGame) {
            return true;
        }
        return false;
    }, [miningHistory, activeMiniGame]);

    const startMiniGame = useCallback((type: MiniGameType) => {
        setActiveMiniGame(type);
        setMiniGameScore(0);
        setMiningHistory([]); // 리셋

        if (type === 'hidden_picture') {
            const gridSize = 6;
            const targetEmoji = HIDDEN_PICTURE_EMOJIS[Math.floor(Math.random() * HIDDEN_PICTURE_EMOJIS.length)];
            const targetCount = Math.floor(Math.random() * 3) + 3; // 3-5개

            // 그리드 생성
            const grid: string[][] = [];
            const targetPositions: { x: number; y: number }[] = [];
            const otherEmojis = HIDDEN_PICTURE_EMOJIS.filter(e => e !== targetEmoji);

            for (let y = 0; y < gridSize; y++) {
                grid[y] = [];
                for (let x = 0; x < gridSize; x++) {
                    grid[y][x] = otherEmojis[Math.floor(Math.random() * otherEmojis.length)];
                }
            }

            // 타겟 배치
            for (let i = 0; i < targetCount; i++) {
                let x, y;
                do {
                    x = Math.floor(Math.random() * gridSize);
                    y = Math.floor(Math.random() * gridSize);
                } while (targetPositions.some(p => p.x === x && p.y === y));

                grid[y][x] = targetEmoji;
                targetPositions.push({ x, y });
            }

            setMiniGameData({
                targetEmoji,
                gridSize,
                targetCount,
                foundCount: 0,
                grid,
                targetPositions
            } as HiddenPictureGame);
            setMiniGameTimer(30); // 30초

        } else if (type === 'puzzle') {
            const pieces = [0, 1, 2, 3, 4, 5, 6, 7, 8];
            // 셔플
            for (let i = pieces.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [pieces[i], pieces[j]] = [pieces[j], pieces[i]];
            }

            setMiniGameData({
                pieces,
                correctOrder: [0, 1, 2, 3, 4, 5, 6, 7, 8],
                moves: 0,
                image: PUZZLE_EMOJIS[Math.floor(Math.random() * PUZZLE_EMOJIS.length)]
            } as PuzzleGame);
            setMiniGameTimer(60); // 60초
        }

        addLog(`🎮 미니게임 시작! ${type === 'hidden_picture' ? '숨은 그림을 찾으세요!' : '퍼즐을 맞추세요!'}`, 'special');
    }, [addLog]);

    const completeMiniGame = useCallback((success: boolean) => {
        if (!activeMiniGame) return;

        const baseReward = MINIGAME_REWARDS[activeMiniGame].base;
        const reward = success ? baseReward + miniGameScore * 50 : Math.floor(baseReward * 0.3);

        setResources(prev => ({ ...prev, money: prev.money + reward }));
        addLog(
            success
                ? `🎉 미니게임 성공! +${reward} 오링!`
                : `😅 시간 초과! +${reward} 오링 (위로금)`,
            success ? 'success' : 'warning'
        );

        setActiveMiniGame(null);
        setMiniGameData(null);
        setMiniGameTimer(0);
    }, [activeMiniGame, miniGameScore, addLog]);

    // --- Visual Helpers ---
    const getEraIcon = () => {
        switch (resources.era) {
            case Era.POND: return "🌊";
            case Era.VILLAGE: return "🏘️";
            case Era.CITY: return "🏙️";
            case Era.KINGDOM: return "🏰";
            case Era.EMPIRE: return "👑";
        }
    };

    const getSeasonIcon = () => {
        switch (resources.season) {
            case Season.SPRING: return "🌸";
            case Season.SUMMER: return "☀️";
            case Season.AUTUMN: return "🍂";
            case Season.WINTER: return "❄️";
        }
    };

    const getDuckOutfit = () => {
        if (resources.era === Era.EMPIRE) return "👑";
        if (resources.era === Era.KINGDOM) return "🎩";
        if (resources.era === Era.CITY) return "🧢";
        if (resources.population >= 20) return "🎀";
        return "";
    };

    const getDuckMood = () => {
        if (isOverworked) return "💦";
        if (resources.happiness < 20) return "😢";
        if (resources.happiness < 40) return "😐";
        if (resources.happiness > 80) return "✨";
        return "";
    };

    const getDuckFace = () => {
        if (isOverworked) return "😵";
        if (isClicking) return "🐤";
        if (resources.happiness < 20) return "🍗";
        return duckFace;
    };

    // --- Event Context ---
    const getEventContext = useCallback((): EventContext => ({
        resources,
        buildings,
        characters,
        activeChains,
        completedChains,
        flags,
        triggeredEvents,
        mbti
    }), [resources, buildings, characters, activeChains, completedChains, flags, triggeredEvents, mbti]);

    // --- 🎮 기믹 2: 과로 타이머 ---
    useEffect(() => {
        if (overworkTimer > 0) {
            const timer = setTimeout(() => {
                setOverworkTimer(prev => {
                    if (prev <= 1) {
                        setIsOverworked(false);
                        addLog("😌 오리들이 휴식을 취해 기운을 차렸습니다!", 'success');
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [overworkTimer]);

    // --- 🎮 기믹 3: 세대 갈등 체크 ---
    useEffect(() => {
        if (screen !== 'game') return;

        // 세대 불만족이 심하면 페널티
        if (elderSatisfaction < 20 && Math.random() < 0.1) {
            addLog("👴 원로들이 은퇴를 선언합니다! 수입이 감소합니다.", 'danger');
            setResources(prev => ({ ...prev, money: Math.floor(prev.money * 0.9) }));
        }
        if (youthSatisfaction < 20 && Math.random() < 0.1) {
            const leaving = Math.min(5, Math.floor(resources.population * 0.1));
            addLog(`🐤 젊은 오리 ${leaving}마리가 떠났습니다!`, 'danger');
            setResources(prev => ({ ...prev, population: Math.max(1, prev.population - leaving) }));
        }
    }, [elderSatisfaction, youthSatisfaction, resources.turn]);

    // --- 🎮 기믹 4: 소문 효과 체크 ---
    useEffect(() => {
        if (screen !== 'game') return;

        // 나쁜 소문 3개 → 반란 이벤트
        if (badRumors >= 3) {
            addLog("📢 나쁜 소문이 너무 퍼졌습니다! 반란의 조짐이...", 'danger');
            setResources(prev => ({ ...prev, happiness: Math.max(0, prev.happiness - 15) }));
            setBadRumors(0);
        }

        // 좋은 소문 3개 → 이민자 유입
        if (goodRumors >= 3) {
            const immigrants = Math.floor(Math.random() * 5) + 3;
            addLog(`📢 좋은 소문을 듣고 ${immigrants}마리의 오리가 이주해왔습니다!`, 'success');
            setResources(prev => ({ ...prev, population: prev.population + immigrants }));
            setGoodRumors(0);
        }
    }, [goodRumors, badRumors]);

    // --- Game Loop ---
    useEffect(() => {
        if (isGameOver || screen !== 'game') return;

        const tick = setInterval(() => {
            if (currentEvent) return;

            setResources(prev => {
                const income = calculateIncome();
                let newDay = prev.day + 1;
                let newSeason = prev.season;

                if (newDay > SEASON_LENGTH) {
                    newDay = 1;
                    newSeason = advanceSeason(prev.season);
                    addLog(`${getSeasonName(newSeason)}이 왔습니다!`, 'info', getSeasonIcon());
                }

                const newEra = calculateEra(prev);
                if (newEra !== prev.era) {
                    addLog(`🎉 ${getEraName(newEra)} 시대가 열렸습니다!`, 'special');
                }

                const newResources = {
                    ...prev,
                    money: prev.money + income,
                    turn: prev.turn + 1,
                    day: newDay,
                    season: newSeason,
                    era: newEra
                };

                setStats(s => ({
                    ...s,
                    totalMoneyEarned: s.totalMoneyEarned + income,
                    highestPopulation: Math.max(s.highestPopulation, prev.population),
                    highestMoney: Math.max(s.highestMoney, newResources.money)
                }));

                return newResources;
            });

            // 세대 만족도 자연 회복
            setElderSatisfaction(prev => Math.min(100, prev + 0.5));
            setYouthSatisfaction(prev => Math.min(100, prev + 0.5));

            if (Math.random() < EVENT_CHANCE_PER_TICK) {
                triggerRandomEvent();
            }

        }, TICK_RATE_MS);

        return () => clearInterval(tick);
    }, [currentEvent, isGameOver, screen, calculateIncome]);

    // 플레이 시간 추적
    useEffect(() => {
        if (screen !== 'game') return;
        const interval = setInterval(() => {
            setStats(s => ({
                ...s,
                playTimeSeconds: Math.floor((Date.now() - startTimeRef.current) / 1000)
            }));
        }, 1000);
        return () => clearInterval(interval);
    }, [screen]);

    // ⛏️ 자동 채굴 효과
    useEffect(() => {
        if (screen !== 'game' || isGameOver) return;

        const autoMining = calculateAutoMining();
        if (autoMining <= 0) return;

        const interval = setInterval(() => {
            if (!activeMiniGame) {
                setResources(prev => ({ ...prev, money: prev.money + autoMining }));
                setStats(prev => ({ ...prev, totalMoneyEarned: prev.totalMoneyEarned + autoMining }));
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [screen, isGameOver, calculateAutoMining, activeMiniGame]);

    // 🎮 미니게임 타이머
    useEffect(() => {
        if (!activeMiniGame || miniGameTimer <= 0) return;

        const interval = setInterval(() => {
            setMiniGameTimer(prev => {
                if (prev <= 1) {
                    completeMiniGame(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [activeMiniGame, miniGameTimer, completeMiniGame]);

    // 팁 로테이션
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTip(prev => (prev + 1) % TIPS.length);
        }, 15000);
        return () => clearInterval(interval);
    }, []);

    // 🎉 마일스톤 체크
    useEffect(() => {
        if (screen !== 'game') return;

        // 돈 마일스톤
        const moneyMilestones = [1000, 10000, 100000, 1000000];
        for (const milestone of moneyMilestones) {
            if (resources.money >= milestone && lastMilestones.current.money < milestone) {
                lastMilestones.current.money = milestone;
                setCelebration({
                    type: 'money',
                    message: `${milestone.toLocaleString()} 오링 달성!`,
                    icon: milestone >= 100000 ? '💎' : milestone >= 10000 ? '💰' : '🪙'
                });
                addLog(`🎊 축하합니다! ${milestone.toLocaleString()} 오링을 모았습니다!`, 'special');
                setTimeout(() => setCelebration(null), 3000);
                break;
            }
        }

        // 인구 마일스톤
        const popMilestones = [50, 100, 200, 500, 1000];
        for (const milestone of popMilestones) {
            if (resources.population >= milestone && lastMilestones.current.population < milestone) {
                lastMilestones.current.population = milestone;
                setCelebration({
                    type: 'population',
                    message: `인구 ${milestone}마리 달성!`,
                    icon: '🦆'
                });
                addLog(`🎊 축하합니다! 인구가 ${milestone}마리가 되었습니다!`, 'special');
                setTimeout(() => setCelebration(null), 3000);
                break;
            }
        }
    }, [resources.money, resources.population, screen]);

    // 🎉 시대 전환 축하
    useEffect(() => {
        if (screen !== 'game' || resources.era === Era.POND) return;

        setCelebration({
            type: 'era',
            message: `${getEraName(resources.era)} 시대!`,
            icon: getEraIcon()
        });
        setTimeout(() => setCelebration(null), 3500);
    }, [resources.era]);

    // 콤보 리셋 타이머
    useEffect(() => {
        if (comboCount > 0) {
            const timer = setTimeout(() => {
                setComboCount(0);
                setShowCombo(false);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [lastClickTime, comboCount]);

    // 엔딩 체크
    useEffect(() => {
        if (isGameOver || screen !== 'game') return;

        const context = getEventContext();
        const ending = checkEndings(context);

        if (ending) {
            setCurrentEnding(ending);
            setIsGameOver(true);
            setScreen('ending');

            const endingData = ENDINGS[ending];

            // 💝 위로 보너스: 오링 < 100,000 시 10% 보너스
            let finalMoneyEarned = stats.totalMoneyEarned;
            if (resources.money < 100000) {
                const consolationBonus = Math.floor(resources.money * 0.1);
                finalMoneyEarned += consolationBonus;
                setResources(prev => ({ ...prev, money: prev.money + consolationBonus }));
                addLog(`💝 위로 보너스! +${consolationBonus.toLocaleString()} 오링`, 'special');
            }

            // 하드모드 점수 보너스 50%
            const difficultyBonus = difficulty === 'hard' ? 1.5 : 1.0;
            const score = Math.floor(calculateScore(
                finalMoneyEarned,
                resources.happiness,
                stats.highestPopulation,
                endingData.isPositive
            ) * difficultyBonus);
            // 로컬 랭킹 저장
            saveRanking({
                name: playerName,
                score,
                money: finalMoneyEarned,
                happiness: resources.happiness,
                population: stats.highestPopulation,
                ending: endingData.name,
                mbti: getMBTIDisplay(mbti),
                date: new Date().toLocaleDateString('ko-KR'),
                difficulty
            });
            setRankingsEasy(loadRankings('easy'));
            setRankingsHard(loadRankings('hard'));

            // 글로벌 랭킹 등록
            submitGlobalRanking({
                player_name: playerName,
                score,
                money: finalMoneyEarned,
                happiness: resources.happiness,
                population: stats.highestPopulation,
                ending: endingData.name,
                mbti: getMBTIDisplay(mbti),
                difficulty
            }).then(result => {
                if (result.success && result.rank) {
                    setGlobalRank(result.rank);
                    addLog(`🌍 전 세계 ${result.rank}위 달성!`, 'special');
                }
                // 글로벌 랭킹 갱신
                fetchGlobalRankings(difficulty, 20).then(data => {
                    if (difficulty === 'easy') {
                        setGlobalRankingsEasy(data);
                    } else {
                        setGlobalRankingsHard(data);
                    }
                });
            });
        }
    }, [resources, flags, completedChains, isGameOver, screen, getEventContext]);

    // --- 저장 데이터 확인 (닉네임 입력 시 실시간 확인) ---
    useEffect(() => {
        if (screen !== 'title') return; // 타이틀 화면에서만 확인
        if (nameInput.trim().length > 0) {
            const hasData = hasSaveData(nameInput.trim());
            setHasSave(hasData);
        } else {
            setHasSave(false);
        }
    }, [nameInput, screen]);

    // --- 자동 저장 (30초마다) ---
    useEffect(() => {
        if (screen !== 'game' || isGameOver) return;

        const saveInterval = setInterval(() => {
            const saveData: SaveData = {
                playerName,
                password: savedPassword,
                difficulty,
                resources,
                buildings,
                characters,
                activeChains,
                completedChains,
                flags: Array.from(flags),
                triggeredEvents: Array.from(triggeredEvents),
                mbti,
                miningUpgrades,
                stats,
                elderSatisfaction,
                youthSatisfaction,
                goodRumors,
                badRumors,
                savedAt: new Date().toISOString()
            };
            saveGame(playerName, saveData);
        }, 30000);

        return () => clearInterval(saveInterval);
    }, [screen, isGameOver, playerName, savedPassword, difficulty, resources, buildings, characters, activeChains, completedChains, flags, triggeredEvents, mbti, miningUpgrades, stats, elderSatisfaction, youthSatisfaction, goodRumors, badRumors]);

    // --- 엔딩 이미지 설정 (훅은 조건부 return 이전에 있어야 함) ---
    useEffect(() => {
        if (currentEnding) {
            setEndingImage(getRandomEndingImage(currentEnding));
            // 저장 데이터 삭제 (엔딩 도달 시)
            deleteSaveData(playerName);
        }
    }, [currentEnding, playerName]);

    // --- 게임 불러오기 ---
    const handleLoadGame = () => {
        const saveData = loadGame(nameInput.trim());
        if (!saveData) return;

        // 비밀번호 확인
        if (saveData.password && saveData.password !== passwordInput) {
            alert('비밀번호가 일치하지 않습니다!');
            return;
        }

        setPlayerName(saveData.playerName);
        setSavedPassword(saveData.password || '');
        setDifficulty(saveData.difficulty);
        setResources(saveData.resources);
        setBuildings(saveData.buildings);
        setCharacters(saveData.characters);
        setActiveChains(saveData.activeChains);
        setCompletedChains(saveData.completedChains);
        setFlags(new Set(saveData.flags));
        setTriggeredEvents(new Set(saveData.triggeredEvents));
        setMbti(saveData.mbti);
        setMiningUpgrades(saveData.miningUpgrades);
        setStats(saveData.stats);
        setElderSatisfaction(saveData.elderSatisfaction);
        setYouthSatisfaction(saveData.youthSatisfaction);
        setGoodRumors(saveData.goodRumors);
        setBadRumors(saveData.badRumors);

        setScreen('game');
        startTimeRef.current = Date.now() - (saveData.stats.playTimeSeconds * 1000);
        addLog(`💾 ${saveData.playerName}님의 저장된 왕국을 불러왔습니다!`, 'special');
        addLog(`📅 마지막 저장: ${new Date(saveData.savedAt).toLocaleString('ko-KR')}`, 'info');
        setShowLoadConfirm(false);
        setPasswordInput('');
    };

    // --- 새 게임 시작 ---
    const handleStartNewGame = () => {
        if (nameInput.trim().length === 0) return;

        // 저장 데이터가 있으면 삭제
        if (hasSave) {
            deleteSaveData(nameInput.trim());
        }

        // 상태 초기화
        setResources({
            money: 0,
            population: 5,
            happiness: 50,
            turn: 1,
            era: Era.POND,
            season: Season.SPRING,
            day: 1
        });
        setBuildings(JSON.parse(JSON.stringify(INITIAL_BUILDINGS)));
        setCharacters(JSON.parse(JSON.stringify(INITIAL_CHARACTERS)));
        setActiveChains([]);
        setCompletedChains([]);
        setFlags(new Set());
        setTriggeredEvents(new Set());
        setMbti(INITIAL_MBTI);
        setMiningUpgrades(JSON.parse(JSON.stringify(INITIAL_MINING_UPGRADES)));
        setStats({
            totalClicks: 0,
            totalMoneyEarned: 0,
            totalMoneySpent: 0,
            eventsTriggered: 0,
            chainsCompleted: 0,
            playTimeSeconds: 0,
            highestPopulation: 5,
            highestMoney: 0
        });
        setElderSatisfaction(50);
        setYouthSatisfaction(50);
        setGoodRumors(0);
        setBadRumors(0);
        setLogs([]);

        setPlayerName(nameInput.trim());
        setSavedPassword(passwordInput);
        setScreen('game');
        startTimeRef.current = Date.now();
        addLog(`🦆 ${nameInput.trim()}님의 오링왕국이 시작됩니다!`, 'special');
        setShowLoadConfirm(false);

        // 게임 시작 시 즉시 저장
        const initialSaveData: SaveData = {
            playerName: nameInput.trim(),
            password: passwordInput,
            difficulty,
            resources: {
                money: 0,
                population: 5,
                happiness: 50,
                turn: 1,
                era: Era.POND,
                season: Season.SPRING,
                day: 1
            },
            buildings: JSON.parse(JSON.stringify(INITIAL_BUILDINGS)),
            characters: JSON.parse(JSON.stringify(INITIAL_CHARACTERS)),
            activeChains: [],
            completedChains: [],
            flags: [],
            triggeredEvents: [],
            mbti: INITIAL_MBTI,
            miningUpgrades: JSON.parse(JSON.stringify(INITIAL_MINING_UPGRADES)),
            stats: {
                totalClicks: 0,
                totalMoneyEarned: 0,
                totalMoneySpent: 0,
                eventsTriggered: 0,
                chainsCompleted: 0,
                playTimeSeconds: 0,
                highestPopulation: 5,
                highestMoney: 0
            },
            elderSatisfaction: 50,
            youthSatisfaction: 50,
            goodRumors: 0,
            badRumors: 0,
            savedAt: new Date().toISOString()
        };
        saveGame(nameInput.trim(), initialSaveData);
    };

    // --- Game Start ---
    const handleStartGame = () => {
        if (nameInput.trim().length === 0) return;

        // 저장 데이터가 있으면 확인 창 표시
        if (hasSave) {
            setShowLoadConfirm(true);
            return;
        }

        handleStartNewGame();
    };

    // --- 수동 저장 ---
    const handleManualSave = () => {
        const saveData: SaveData = {
            playerName,
            password: savedPassword,
            difficulty,
            resources,
            buildings,
            characters,
            activeChains,
            completedChains,
            flags: Array.from(flags),
            triggeredEvents: Array.from(triggeredEvents),
            mbti,
            miningUpgrades,
            stats: {
                ...stats,
                playTimeSeconds: Math.floor((Date.now() - startTimeRef.current) / 1000)
            },
            elderSatisfaction,
            youthSatisfaction,
            goodRumors,
            badRumors,
            savedAt: new Date().toISOString()
        };
        saveGame(playerName, saveData);
        setShowSaveSuccess(true);
        setTimeout(() => setShowSaveSuccess(false), 2000);
        addLog('💾 게임이 저장되었습니다!', 'success');
    };

    // --- Screenshot & Share ---
    const handleScreenshot = async () => {
        if (!endingCardRef.current) return;
        try {
            const canvas = await html2canvas(endingCardRef.current, {
                backgroundColor: '#1e293b',
                scale: 2
            });
            const link = document.createElement('a');
            link.download = `오링왕국_${playerName}_${new Date().toLocaleDateString('ko-KR')}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (error) {
            console.error('Screenshot failed:', error);
        }
    };

    const handleShare = async () => {
        const shareText = `오링왕국에서 "${ENDINGS[currentEnding!]?.name}" 엔딩 달성! 점수: ${calculateScore(stats.totalMoneyEarned, resources.happiness, stats.highestPopulation, ENDINGS[currentEnding!]?.isPositive || false).toLocaleString()}점 | DUCKTI: ${calculateMBTIType(mbti)}`;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: '오링왕국 결과',
                    text: shareText,
                    url: window.location.href
                });
            } catch (error) {
                if ((error as Error).name !== 'AbortError') {
                    await navigator.clipboard.writeText(shareText);
                    alert('결과가 클립보드에 복사되었습니다!');
                }
            }
        } else {
            await navigator.clipboard.writeText(shareText);
            alert('결과가 클립보드에 복사되었습니다!');
        }
    };

    // --- 🎮 기믹 2: 클릭 과로 체크 ---
    const checkOverwork = () => {
        const now = Date.now();
        const recentClicks = clickTimestamps.filter(t => now - t < 3000);
        setClickTimestamps([...recentClicks, now]);

        const threshold = diffSettings.clickFatigueThreshold;
        if (recentClicks.length >= threshold && !isOverworked) {
            // 랜덤 확률로 과로 발생 (이지: 10%, 하드: 30%)
            const overworkChance = difficulty === 'hard' ? 0.3 : 0.1;
            if (Math.random() < overworkChance) {
                setIsOverworked(true);
                setOverworkTimer(difficulty === 'hard' ? 12 : 8);
                const penalty = difficulty === 'hard' ? 4 : 2;
                setResources(prev => ({ ...prev, happiness: Math.max(0, prev.happiness - penalty) }));
                const messages = [
                    "😵 왕이 너무 다그쳐요... 오리들이 지쳤습니다!",
                    "💦 오리들이 힘들어해요! 잠시 쉬어가세요~",
                    "😰 너무 빠른 클릭! 오리들이 헉헉거립니다!",
                    "🥴 오리들: '우리도 쉬고 싶어요...'",
                ];
                addLog(`${messages[Math.floor(Math.random() * messages.length)]} (${difficulty === 'hard' ? 12 : 8}초간 효율 50%↓)`, 'warning');
                return true;
            }
        }

        // 적절한 페이스 보너스 (가끔)
        if (recentClicks.length >= 3 && recentClicks.length <= 6 && Math.random() < 0.02) {
            setResources(prev => ({ ...prev, happiness: Math.min(100, prev.happiness + 1) }));
            addLog("😊 여유있는 왕이시네요~ 오리들이 기뻐합니다!", 'success');
        }

        return false;
    };

    // --- Interactions ---
    const handleManualGather = (e: React.MouseEvent) => {
        if (isGameOver || activeMiniGame) return;

        // 🎮 기믹 2: 과로 체크 (패널티는 주지만 클릭은 막지 않음!)
        checkOverwork();

        // 🎉 콤보 시스템
        const now = Date.now();
        if (now - lastClickTime < 2000) {
            setComboCount(prev => Math.min(prev + 1, 10));
            setShowCombo(true);
        } else {
            setComboCount(1);
        }
        setLastClickTime(now);

        // 채굴량 계산 (업그레이드 적용)
        const { value: clickValue, isLucky } = calculateClickValue();

        // 콤보 보너스: +5% per combo (최대 +50%)
        const comboBonus = 1 + (comboCount * 0.05);

        // 과로 시 효율 50% 감소
        const finalValue = isOverworked
            ? Math.max(1, Math.floor(clickValue * 0.5 * comboBonus))
            : Math.floor(clickValue * comboBonus);

        setIsClicking(true);
        setTimeout(() => setIsClicking(false), 150);

        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const el = document.createElement('div');
        let displayText = isLucky ? `🍀+${finalValue}` : (finalValue > 1 ? `+${finalValue}` : "🍩");
        if (Math.random() > 0.85) displayText = "꽥!";
        el.innerText = displayText;
        el.className = "absolute font-bold pointer-events-none animate-float select-none";
        el.className += isLucky ? " text-green-400 text-3xl" : (finalValue > 5 ? " text-yellow-300 text-2xl" : " text-yellow-400 text-xl");
        el.style.left = `${x}px`;
        el.style.top = `${y}px`;
        clickAreaRef.current?.appendChild(el);
        setTimeout(() => el.remove(), 800);

        setResources(prev => ({ ...prev, money: prev.money + finalValue }));
        setStats(prev => ({
            ...prev,
            totalClicks: prev.totalClicks + 1,
            totalMoneyEarned: prev.totalMoneyEarned + finalValue
        }));

        // 채굴 히스토리 추적 (미니게임 트리거용)
        setMiningHistory(prev => {
            const now = Date.now();
            const recent = prev.filter(h => now - h.time < MINIGAME_TRIGGER_WINDOW);
            return [...recent, { time: now, amount: finalValue }];
        });

        // 미니게임 트리거 체크
        if (checkMiniGameTrigger()) {
            const gameType: MiniGameType = Math.random() > 0.5 ? 'hidden_picture' : 'puzzle';
            startMiniGame(gameType);
        }
    };

    // 채굴 업그레이드 구매
    const handleUpgradePurchase = (type: MiningUpgradeType) => {
        const upgrade = miningUpgrades[type];
        if (upgrade.level >= upgrade.maxLevel) {
            addLog(`${upgrade.name}이(가) 이미 최대 레벨입니다!`, 'warning');
            return;
        }

        const cost = getUpgradeCost(type);
        if (resources.money >= cost) {
            setResources(prev => ({ ...prev, money: prev.money - cost }));
            setMiningUpgrades(prev => ({
                ...prev,
                [type]: { ...prev[type], level: prev[type].level + 1 }
            }));
            addLog(`${upgrade.icon} ${upgrade.name} 업그레이드! (Lv.${upgrade.level + 1})`, 'success');
        } else {
            addLog("오링이 부족합니다!", 'warning');
        }
    };

    const handlePurchase = (type: BuildingType) => {
        const building = buildings[type];
        const eraOrder = { [Era.POND]: 0, [Era.VILLAGE]: 1, [Era.CITY]: 2, [Era.KINGDOM]: 3, [Era.EMPIRE]: 4 };
        if (eraOrder[resources.era] < eraOrder[building.unlockEra]) {
            addLog(`${getEraName(building.unlockEra)} 시대에 해금됩니다!`, 'warning');
            return;
        }
        if (building.count >= building.maxCount) {
            addLog(`${building.name}의 최대 개수에 도달했습니다!`, 'warning');
            return;
        }

        const cost = Math.floor(building.baseCost * Math.pow(building.costMultiplier, building.count));

        // 도시 시대 이후 건물은 인구 보너스가 배수로 증가
        const isHighTierBuilding = [
            BuildingType.HOSPITAL, BuildingType.MARKET, BuildingType.CASTLE,
            BuildingType.HARBOR, BuildingType.TEMPLE, BuildingType.WONDER
        ].includes(type);

        const populationBonus = isHighTierBuilding && building.populationBonus > 0
            ? Math.floor(building.populationBonus * Math.pow(2, building.count))
            : building.populationBonus;

        if (resources.money >= cost) {
            setResources(prev => ({
                ...prev,
                money: prev.money - cost,
                population: prev.population + populationBonus,
                happiness: Math.min(100, prev.happiness + building.happinessBonus)
            }));
            setBuildings(prev => ({
                ...prev,
                [type]: { ...prev[type], count: prev[type].count + 1 }
            }));
            setStats(prev => ({ ...prev, totalMoneySpent: prev.totalMoneySpent + cost }));

            // 인구 보너스 표시
            const popMsg = populationBonus > 0 ? ` (+${populationBonus}명)` : '';
            addLog(`${building.icon} ${building.name} 건설 완료!${popMsg}`, 'success');
        } else {
            addLog("오링이 부족합니다!", 'warning');
        }
    };

    const triggerRandomEvent = () => {
        const context = getEventContext();
        const availableEvents = filterAvailableEvents(ALL_EVENTS, context);
        const event = selectRandomEvent(availableEvents, context);

        if (event) {
            setCurrentEvent({ ...event, id: `${event.id}_${Date.now()}` });
            setStats(prev => ({ ...prev, eventsTriggered: prev.eventsTriggered + 1 }));
            if (event.oneTime) {
                setTriggeredEvents(prev => new Set(prev).add(event.id));
            }
        }
    };

    const handleEventOption = (option: GameEventOption) => {
        const context = getEventContext();
        const result = processEventOption(option, context);

        // 🎮 기믹 1: 연속 선택 피로도 체크
        const choiceType = classifyChoice(option);
        const fatigueCount = diffSettings.choiceFatigueCount;
        const newRecentChoices = [...recentChoices, choiceType].slice(-fatigueCount);
        setRecentChoices(newRecentChoices);

        // 같은 유형 연속 체크 (난이도에 따라 기준 다름)
        const allSame = newRecentChoices.length >= fatigueCount &&
            newRecentChoices.every(c => c === choiceType) &&
            choiceType !== 'neutral';

        if (allSame) {
            const fatigueMessages: Record<ChoiceType, string> = {
                saving: "👛 또 절약이야? 왕이 너무 짠돌이야!",
                spending: "💸 맨날 돈만 쓰네! 나라 망하겠다!",
                peaceful: "😤 너무 유약해! 우리를 지켜줘!",
                aggressive: "⚔️ 전쟁만 하면 뭐해! 평화를 원해!",
                traditional: "👴 변화가 필요해요! 너무 구식이야!",
                innovative: "🐤 전통을 무시하지 마세요!",
                neutral: ""
            };
            addLog(fatigueMessages[choiceType], 'warning');
            const penalty = difficulty === 'hard' ? 8 : 5;
            setResources(prev => ({ ...prev, happiness: Math.max(0, prev.happiness - penalty) }));
            setChoiceFatigue(prev => prev + 1);
        } else if (newRecentChoices.length >= 3 && new Set(newRecentChoices).size >= 3) {
            // 다양한 선택 보너스
            const bonusChance = difficulty === 'easy' ? 0.4 : 0.2;
            if (Math.random() < bonusChance) {
                addLog("⚖️ 균형잡힌 왕이시네요! 오리들이 존경합니다!", 'success');
                setResources(prev => ({ ...prev, happiness: Math.min(100, prev.happiness + 3) }));
            }
        }

        // 🎮 기믹 3: 세대 갈등 처리
        if (choiceType === 'traditional') {
            setElderSatisfaction(prev => Math.min(100, prev + 10));
            setYouthSatisfaction(prev => Math.max(0, prev - 8));
            if (youthSatisfaction > 30) {
                addLog("👴 원로들이 기뻐합니다! / 🐤 젊은이들이 한숨을 쉽니다.", 'info');
            }
        } else if (choiceType === 'innovative') {
            setYouthSatisfaction(prev => Math.min(100, prev + 10));
            setElderSatisfaction(prev => Math.max(0, prev - 8));
            if (elderSatisfaction > 30) {
                addLog("🐤 젊은이들이 환호합니다! / 👴 원로들이 고개를 젓습니다.", 'info');
            }
        }

        // 🎮 기믹 4: 소문 시스템 (난이도별 확률)
        if (Math.random() < diffSettings.rumorChance) {
            const isGoodResult = (result.resourceChanges.money || 0) >= 0 &&
                (result.resourceChanges.happiness || 0) >= 0;

            // 난이도별 왜곡 확률
            if (Math.random() < diffSettings.rumorDistortChance) {
                // 결과와 반대로 소문
                if (isGoodResult) {
                    const rumor = BAD_RUMORS[Math.floor(Math.random() * BAD_RUMORS.length)];
                    setBadRumors(prev => prev + 1);
                    addLog(`📢 [소문] ${rumor}`, 'warning');
                    setLastRumorMessage(rumor);
                } else {
                    const rumor = GOOD_RUMORS[Math.floor(Math.random() * GOOD_RUMORS.length)];
                    setGoodRumors(prev => prev + 1);
                    addLog(`📢 [소문] ${rumor}`, 'info');
                    setLastRumorMessage(rumor);
                }
            } else {
                // 결과대로 소문
                if (isGoodResult) {
                    const rumor = GOOD_RUMORS[Math.floor(Math.random() * GOOD_RUMORS.length)];
                    setGoodRumors(prev => prev + 1);
                    addLog(`📢 [소문] ${rumor}`, 'success');
                } else {
                    const rumor = BAD_RUMORS[Math.floor(Math.random() * BAD_RUMORS.length)];
                    setBadRumors(prev => prev + 1);
                    addLog(`📢 [소문] ${rumor}`, 'danger');
                }
            }
        }

        // 자원 변경 적용
        const defense = getTotalDefense();
        let actualMoneyChange = result.resourceChanges.money || 0;
        let actualPopChange = result.resourceChanges.population || 0;
        let actualHappinessChange = result.resourceChanges.happiness || 0;

        // 오링 페널티: 시대별 기하급수적 증가
        // 연못=1배, 마을=3배, 도시=10배, 왕국=30배, 제국=100배
        if (actualMoneyChange < 0) {
            const eraMultiplier: Record<Era, number> = {
                [Era.POND]: 1,
                [Era.VILLAGE]: 3,
                [Era.CITY]: 10,
                [Era.KINGDOM]: 30,
                [Era.EMPIRE]: 100
            };
            actualMoneyChange = Math.floor(actualMoneyChange * eraMultiplier[resources.era]);
        }

        // 행복도 증가량 50% 감소
        if (actualHappinessChange > 0) {
            actualHappinessChange = Math.floor(actualHappinessChange * 0.5);
        }

        // 방어력에 따른 피해 감소
        if (actualPopChange < 0) {
            actualPopChange = Math.floor(actualPopChange * (1 - defense / 200));
        }
        if (actualHappinessChange < 0) {
            actualHappinessChange = Math.floor(actualHappinessChange * (1 - defense / 200));
        }

        setResources(prev => ({
            ...prev,
            money: prev.money + actualMoneyChange,
            population: Math.max(0, prev.population + actualPopChange),
            happiness: Math.min(100, Math.max(0, prev.happiness + actualHappinessChange))
        }));

        // MBTI 업데이트 + 시각적 피드백
        if (Object.keys(result.mbtiChanges).length > 0) {
            setMbti(prev => {
                const newMbti = updateMBTI(prev, result.mbtiChanges);
                // MBTI 변화 로그
                const changes: string[] = [];
                if (result.mbtiChanges.E) changes.push(`${result.mbtiChanges.E > 0 ? 'E' : 'I'}+${Math.abs(result.mbtiChanges.E)}`);
                if (result.mbtiChanges.S) changes.push(`${result.mbtiChanges.S > 0 ? 'S' : 'N'}+${Math.abs(result.mbtiChanges.S)}`);
                if (result.mbtiChanges.T) changes.push(`${result.mbtiChanges.T > 0 ? 'T' : 'F'}+${Math.abs(result.mbtiChanges.T)}`);
                if (result.mbtiChanges.J) changes.push(`${result.mbtiChanges.J > 0 ? 'J' : 'P'}+${Math.abs(result.mbtiChanges.J)}`);
                if (changes.length > 0) {
                    addLog(`🧠 DUCKTI: ${changes.join(', ')}`, 'special');
                }
                return newMbti;
            });
        }

        result.newFlags.forEach(flag => {
            setFlags(prev => new Set(prev).add(flag));
        });

        result.unlockedCharacters.forEach(charId => {
            setCharacters(prev => ({
                ...prev,
                [charId]: { ...prev[charId], isUnlocked: true, metAt: resources.turn }
            }));
            const char = INITIAL_CHARACTERS[charId];
            addLog(`🎭 ${char.name}을(를) 만났습니다!`, 'special');
        });

        if (result.chainUpdates.start) {
            const newChain = startChain(result.chainUpdates.start, resources.turn);
            if (newChain) {
                setActiveChains(prev => [...prev, newChain]);
                addLog(`📖 "${newChain.name}" 스토리가 시작됩니다!`, 'special');
            }
        }

        if (result.chainUpdates.advance) {
            setActiveChains(prev => prev.map(chain =>
                chain.id === result.chainUpdates.advance ? advanceChain(chain, option.id) : chain
            ));
        }

        if (result.chainUpdates.end) {
            const endedChain = activeChains.find(c => c.isActive);
            if (endedChain) {
                setActiveChains(prev => prev.filter(c => c.id !== endedChain.id));
                setCompletedChains(prev => [...prev, endedChain.id]);
                setStats(prev => ({ ...prev, chainsCompleted: prev.chainsCompleted + 1 }));
                addLog(`📕 "${endedChain.name}" 스토리가 완료되었습니다!`, 'special');
            }
        }

        if (result.triggeredEnding) {
            setCurrentEnding(result.triggeredEnding);
            setIsGameOver(true);
            setScreen('ending');
        }

        addLog(result.logMessage, result.logType);
        setCurrentEvent(null);
    };

    // ============================================
    // RENDER: Title Screen
    // ============================================
    if (screen === 'title') {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900/20 to-slate-900 text-white flex flex-col items-center justify-center p-4">
                <div className="text-center mb-8">
                    <div className="text-8xl mb-4 animate-bounce">🦆</div>
                    <h1 className="text-5xl font-bold text-yellow-400 mb-2 font-display">오링왕국</h1>
                    <p className="text-slate-400">O-Ring Kingdom</p>
                </div>

                {!showRanking ? (
                    <div className="w-full max-w-md bg-slate-800/80 backdrop-blur rounded-2xl p-6 border-2 border-purple-500/50">
                        <h2 className="text-xl font-bold text-purple-400 mb-4 text-center">🎮 새 게임</h2>

                        <div className="mb-4">
                            <label className="block text-slate-400 text-sm mb-2">오링의 주인 이름</label>
                            <input
                                type="text"
                                value={nameInput}
                                onChange={(e) => setNameInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleStartGame()}
                                placeholder="이름을 입력하세요..."
                                className="w-full bg-slate-700 border-2 border-slate-600 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:border-purple-500 focus:outline-none transition-colors"
                                maxLength={20}
                            />
                            {/* 저장 데이터 표시 */}
                            {hasSave && nameInput.trim().length > 0 && (
                                <div className="mt-2 p-2 bg-green-900/30 border border-green-500/50 rounded-lg text-sm">
                                    <span className="text-green-400">💾 저장된 게임이 있습니다!</span>
                                </div>
                            )}
                        </div>

                        {/* 비밀번호 입력 */}
                        <div className="mb-4">
                            <label className="block text-slate-400 text-sm mb-2">
                                🔒 비밀번호 {hasSave ? '(불러오기용)' : '(선택사항)'}
                            </label>
                            <input
                                type="password"
                                value={passwordInput}
                                onChange={(e) => setPasswordInput(e.target.value)}
                                placeholder={hasSave ? "저장된 비밀번호 입력..." : "비밀번호 설정 (선택)"}
                                className="w-full bg-slate-700 border-2 border-slate-600 rounded-xl px-4 py-2 text-white placeholder-slate-500 focus:border-purple-500 focus:outline-none transition-colors text-sm"
                                maxLength={20}
                            />
                        </div>

                        {/* 난이도 선택 */}
                        <div className="mb-4">
                            <label className="block text-slate-400 text-sm mb-2">난이도 선택</label>
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    onClick={() => setDifficulty('easy')}
                                    className={`p-3 rounded-xl border-2 transition-all ${difficulty === 'easy'
                                        ? 'bg-green-600/30 border-green-500 text-green-300'
                                        : 'bg-slate-700 border-slate-600 text-slate-400 hover:border-slate-500'
                                        }`}
                                >
                                    <div className="text-2xl mb-1">🌸</div>
                                    <div className="font-bold">이지</div>
                                    <div className="text-xs opacity-70">느긋하게 즐기기</div>
                                </button>
                                <button
                                    onClick={() => setDifficulty('hard')}
                                    className={`p-3 rounded-xl border-2 transition-all ${difficulty === 'hard'
                                        ? 'bg-red-600/30 border-red-500 text-red-300'
                                        : 'bg-slate-700 border-slate-600 text-slate-400 hover:border-slate-500'
                                        }`}
                                >
                                    <div className="text-2xl mb-1">🔥</div>
                                    <div className="font-bold">하드</div>
                                    <div className="text-xs opacity-70">점수 1.5배!</div>
                                </button>
                            </div>
                        </div>

                        <button
                            onClick={handleStartGame}
                            disabled={nameInput.trim().length === 0}
                            className={`w-full py-4 rounded-xl font-bold text-xl transition-all shadow-lg ${difficulty === 'hard'
                                ? 'bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 shadow-red-500/25'
                                : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 shadow-purple-500/25'
                                } disabled:from-slate-600 disabled:to-slate-600 text-white`}
                        >
                            {difficulty === 'hard' ? '🔥' : '🚀'} 모험 시작!
                        </button>

                        <button
                            onClick={() => setShowRanking(true)}
                            className="w-full mt-4 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-xl font-bold transition-colors"
                        >
                            🏆 랭킹 보기
                        </button>

                        <div className="mt-4 p-3 bg-slate-900/50 rounded-xl text-xs text-slate-400">
                            <div className="font-bold text-purple-400 mb-2">⚡ 게임 기믹 {difficulty === 'hard' && <span className="text-red-400">(하드모드 강화!)</span>}</div>
                            <ul className="space-y-1">
                                <li>🔄 같은 선택 {diffSettings.choiceFatigueCount}연속 시 오리들이 반발!</li>
                                <li>🖱️ 3초 내 {diffSettings.clickFatigueThreshold}클릭 시 과로</li>
                                <li>👴🐤 원로와 젊은이의 세대 갈등 관리</li>
                                <li>📢 {Math.floor(diffSettings.rumorChance * 100)}% 확률로 소문 발생</li>
                            </ul>
                        </div>
                    </div>
                ) : (
                    <div className="w-full max-w-lg bg-slate-800/80 backdrop-blur rounded-2xl p-6 border-2 border-yellow-500/50 max-h-[70vh] overflow-y-auto">
                        <h2 className="text-2xl font-bold text-yellow-400 mb-4 text-center">🏆 명예의 전당</h2>

                        {/* 난이도 탭 */}
                        <div className="flex gap-2 mb-4">
                            <button
                                onClick={() => setRankingTab('easy')}
                                className={`flex-1 py-2 rounded-lg font-bold transition-all ${rankingTab === 'easy'
                                    ? 'bg-green-600 text-white'
                                    : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                                    }`}
                            >
                                🌸 이지
                            </button>
                            <button
                                onClick={() => setRankingTab('hard')}
                                className={`flex-1 py-2 rounded-lg font-bold transition-all ${rankingTab === 'hard'
                                    ? 'bg-red-600 text-white'
                                    : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                                    }`}
                            >
                                🔥 하드
                            </button>
                        </div>

                        {(rankingTab === 'easy' ? rankingsEasy : rankingsHard).length === 0 ? (
                            <p className="text-center text-slate-500 py-8">아직 기록이 없습니다.</p>
                        ) : (
                            <div className="space-y-2">
                                {(rankingTab === 'easy' ? rankingsEasy : rankingsHard).map((entry, idx) => (
                                    <div key={idx} className={`flex items-center gap-3 p-3 rounded-xl ${idx === 0 ? 'bg-yellow-900/30 border border-yellow-500/50' :
                                        idx === 1 ? 'bg-slate-600/30 border border-slate-400/50' :
                                            idx === 2 ? 'bg-orange-900/30 border border-orange-500/50' :
                                                'bg-slate-700/30'
                                        }`}>
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${idx === 0 ? 'bg-yellow-500 text-yellow-900' :
                                            idx === 1 ? 'bg-slate-400 text-slate-900' :
                                                idx === 2 ? 'bg-orange-500 text-orange-900' :
                                                    'bg-slate-600 text-slate-300'
                                            }`}>
                                            {idx + 1}
                                        </div>
                                        <div className="flex-1">
                                            <div className="font-bold text-white">{entry.name}</div>
                                            <div className="text-xs text-slate-400">
                                                {entry.ending} • {entry.mbti} • {entry.date}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-bold text-yellow-400">{entry.score.toLocaleString()}</div>
                                            <div className="text-xs text-slate-500">점</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <button
                            onClick={() => setShowRanking(false)}
                            className="w-full mt-4 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-xl font-bold transition-colors"
                        >
                            ← 돌아가기
                        </button>
                    </div>
                )}

                {/* 저장 데이터 불러오기 확인 팝업 */}
                {showLoadConfirm && (
                    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
                        <div className="w-full max-w-sm bg-slate-800 rounded-2xl p-6 border-2 border-green-500">
                            <h3 className="text-xl font-bold text-green-400 mb-4 text-center">💾 저장된 게임 발견!</h3>
                            <p className="text-slate-300 text-center mb-4">
                                <span className="font-bold text-white">{nameInput}</span>님의 저장된 왕국이 있습니다.<br/>
                                이어서 플레이하시겠습니까?
                            </p>
                            {hasPassword(nameInput.trim()) && (
                                <div className="mb-4 p-2 bg-yellow-900/30 border border-yellow-500/50 rounded-lg text-center">
                                    <span className="text-yellow-400 text-sm">🔒 비밀번호가 필요합니다</span>
                                    {!passwordInput && (
                                        <p className="text-yellow-300 text-xs mt-1">위에서 비밀번호를 입력해주세요</p>
                                    )}
                                </div>
                            )}
                            <div className="flex gap-2">
                                <button
                                    onClick={handleLoadGame}
                                    className="flex-1 bg-green-600 hover:bg-green-500 text-white py-3 rounded-xl font-bold transition-colors"
                                >
                                    📂 불러오기
                                </button>
                                <button
                                    onClick={handleStartNewGame}
                                    className="flex-1 bg-red-600 hover:bg-red-500 text-white py-3 rounded-xl font-bold transition-colors"
                                >
                                    🆕 새로 시작
                                </button>
                            </div>
                            <button
                                onClick={() => setShowLoadConfirm(false)}
                                className="w-full mt-3 bg-slate-700 hover:bg-slate-600 text-white py-2 rounded-xl font-bold transition-colors"
                            >
                                취소
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ============================================
    // RENDER: Ending Screen
    // ============================================
    if (screen === 'ending' && currentEnding) {
        const ending = ENDINGS[currentEnding];
        const endingData = generateEndingScreen(currentEnding, mbti, {
            totalTurns: resources.turn,
            totalMoney: stats.totalMoneyEarned,
            maxPopulation: stats.highestPopulation,
            eventsTriggered: stats.eventsTriggered,
            chainsCompleted: stats.chainsCompleted,
            playTimeSeconds: stats.playTimeSeconds
        });
        const score = calculateScore(stats.totalMoneyEarned, resources.happiness, stats.highestPopulation, ending.isPositive);

        return (
            <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center justify-center p-4 overflow-y-auto">
                <div ref={endingCardRef} className="max-w-2xl w-full bg-slate-800 rounded-2xl p-6 border-2 border-purple-500 my-4">
                    {/* 엔딩 이미지 */}
                    {endingImage && (
                        <div className="relative w-full h-48 mb-4 rounded-xl overflow-hidden">
                            <img
                                src={endingImage}
                                alt={ending.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = '/assets/events/daily_sunny_day.webp';
                                }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-800 via-transparent to-transparent"></div>
                        </div>
                    )}

                    <div className="text-center mb-6">
                        <div className="text-6xl mb-3">{ending.icon}</div>
                        <h1 className="text-3xl font-bold text-purple-400 mb-1">{ending.name}</h1>
                        <p className="text-slate-400 text-sm">{ending.description}</p>
                        <div className="mt-3 text-yellow-400 font-bold text-2xl">
                            점수: {score.toLocaleString()}점
                        </div>
                    </div>

                    <div className="bg-slate-900 rounded-xl p-4 mb-4 whitespace-pre-line text-slate-300 text-sm leading-relaxed max-h-48 overflow-y-auto">
                        {ending.epilogue}
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="bg-slate-700 rounded-lg p-3">
                            <div className="text-slate-400 text-xs">플레이어</div>
                            <div className="font-bold">{playerName}</div>
                        </div>
                        <div className="bg-slate-700 rounded-lg p-3">
                            <div className="text-slate-400 text-xs">DUCKTI</div>
                            <div className="font-bold text-purple-400">{endingData.mbtiType}</div>
                        </div>
                        <div className="bg-slate-700 rounded-lg p-3">
                            <div className="text-slate-400 text-xs">세대 조화</div>
                            <div className="font-bold">👴{elderSatisfaction}% / 🐤{youthSatisfaction}%</div>
                        </div>
                        <div className="bg-slate-700 rounded-lg p-3">
                            <div className="text-slate-400 text-xs">선택 피로도</div>
                            <div className="font-bold">{choiceFatigue}회 반발</div>
                        </div>
                    </div>

                    <div className="flex gap-2 mb-3">
                        <button
                            onClick={handleScreenshot}
                            className="flex-1 bg-blue-600 hover:bg-blue-500 text-white py-3 rounded-xl font-bold transition-colors"
                        >
                            📸 저장
                        </button>
                        <button
                            onClick={handleShare}
                            className="flex-1 bg-green-600 hover:bg-green-500 text-white py-3 rounded-xl font-bold transition-colors"
                        >
                            📤 공유
                        </button>
                    </div>

                    <div className="flex gap-2">
                        <button
                            onClick={() => setShowRanking(true)}
                            className="flex-1 bg-yellow-600 hover:bg-yellow-500 text-white py-3 rounded-xl font-bold transition-colors"
                        >
                            🏆 랭킹
                        </button>
                        <button
                            onClick={() => window.location.reload()}
                            className="flex-1 bg-purple-600 hover:bg-purple-500 text-white py-3 rounded-xl font-bold transition-colors"
                        >
                            🔄 다시 시작
                        </button>
                    </div>
                </div>

                {showRanking && (
                    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
                        <div className="w-full max-w-lg bg-slate-800 rounded-2xl p-6 border-2 border-yellow-500 max-h-[80vh] overflow-y-auto">
                            <h2 className="text-2xl font-bold text-yellow-400 mb-4 text-center">🏆 명예의 전당</h2>

                            {/* 글로벌 랭크 표시 */}
                            {globalRank && (
                                <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-3 mb-4 text-center">
                                    <span className="text-white font-bold">🌍 전 세계 {globalRank}위!</span>
                                </div>
                            )}

                            {/* 로컬/글로벌 탭 */}
                            <div className="flex gap-2 mb-3">
                                <button
                                    onClick={() => setRankingMode('local')}
                                    className={`flex-1 py-2 rounded-lg font-bold transition-all ${rankingMode === 'local'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-slate-700 text-slate-400'
                                        }`}
                                >
                                    💾 로컬
                                </button>
                                <button
                                    onClick={() => setRankingMode('global')}
                                    className={`flex-1 py-2 rounded-lg font-bold transition-all ${rankingMode === 'global'
                                        ? 'bg-purple-600 text-white'
                                        : 'bg-slate-700 text-slate-400'
                                        }`}
                                >
                                    🌍 글로벌
                                </button>
                            </div>

                            {/* 난이도 탭 */}
                            <div className="flex gap-2 mb-4">
                                <button
                                    onClick={() => setRankingTab('easy')}
                                    className={`flex-1 py-2 rounded-lg font-bold transition-all ${rankingTab === 'easy'
                                        ? 'bg-green-600 text-white'
                                        : 'bg-slate-700 text-slate-400'
                                        }`}
                                >
                                    🌸 이지
                                </button>
                                <button
                                    onClick={() => setRankingTab('hard')}
                                    className={`flex-1 py-2 rounded-lg font-bold transition-all ${rankingTab === 'hard'
                                        ? 'bg-red-600 text-white'
                                        : 'bg-slate-700 text-slate-400'
                                        }`}
                                >
                                    🔥 하드
                                </button>
                            </div>

                            <div className="space-y-2">
                                {rankingMode === 'local' ? (
                                    (rankingTab === 'easy' ? rankingsEasy : rankingsHard).map((entry, idx) => (
                                        <div key={idx} className={`flex items-center gap-3 p-3 rounded-xl ${entry.name === playerName && entry.score === score ? 'ring-2 ring-purple-500 bg-purple-900/30' :
                                            idx === 0 ? 'bg-yellow-900/30' : 'bg-slate-700/30'
                                            }`}>
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${idx === 0 ? 'bg-yellow-500 text-yellow-900' :
                                                idx === 1 ? 'bg-slate-400 text-slate-900' :
                                                    idx === 2 ? 'bg-orange-500 text-orange-900' : 'bg-slate-600'
                                                }`}>{idx + 1}</div>
                                            <div className="flex-1">
                                                <div className="font-bold">{entry.name}</div>
                                                <div className="text-xs text-slate-400">{entry.ending}</div>
                                            </div>
                                            <div className="font-bold text-yellow-400">{entry.score.toLocaleString()}</div>
                                        </div>
                                    ))
                                ) : (
                                    (rankingTab === 'easy' ? globalRankingsEasy : globalRankingsHard).length > 0 ? (
                                        (rankingTab === 'easy' ? globalRankingsEasy : globalRankingsHard).map((entry, idx) => (
                                            <div key={idx} className={`flex items-center gap-3 p-3 rounded-xl ${entry.player_name === playerName ? 'ring-2 ring-purple-500 bg-purple-900/30' :
                                                idx === 0 ? 'bg-yellow-900/30' : 'bg-slate-700/30'
                                                }`}>
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${idx === 0 ? 'bg-yellow-500 text-yellow-900' :
                                                    idx === 1 ? 'bg-slate-400 text-slate-900' :
                                                        idx === 2 ? 'bg-orange-500 text-orange-900' : 'bg-slate-600'
                                                    }`}>{idx + 1}</div>
                                                <div className="flex-1">
                                                    <div className="font-bold">{entry.player_name}</div>
                                                    <div className="text-xs text-slate-400">{entry.ending} • {entry.mbti}</div>
                                                </div>
                                                <div className="font-bold text-yellow-400">{entry.score.toLocaleString()}</div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center text-slate-400 py-8">
                                            글로벌 랭킹을 불러올 수 없습니다.<br />
                                            <span className="text-xs">API 서버 설정이 필요합니다.</span>
                                        </div>
                                    )
                                )}
                            </div>
                            <button onClick={() => setShowRanking(false)} className="w-full mt-4 bg-slate-700 hover:bg-slate-600 py-3 rounded-xl font-bold">
                                닫기
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // --- Visual Helpers (Assets) ---
    const getEraBackground = (era: Era) => {
        switch (era) {
            case Era.POND: return '/assets/backgrounds/bg_pond.webp';
            case Era.VILLAGE: return '/assets/backgrounds/bg_village.webp';
            case Era.CITY: return '/assets/backgrounds/bg_city.webp';
            case Era.KINGDOM: return '/assets/backgrounds/bg_kingdom.webp';
            case Era.EMPIRE: return '/assets/backgrounds/bg_empire.webp';
            default: return '/assets/backgrounds/bg_pond.webp';
        }
    };

    const getEventIllustration = (category: string, eventId?: string) => {
        // 이벤트 ID 기반 이미지 매핑
        const eventImageMap: Record<string, string> = {
            // Daily events
            'sunny_day': 'daily_sunny_day', 'baby_swim': 'daily_baby_swim',
            'bread_crumbs': 'daily_bread_crumbs', 'construction': 'daily_construction',
            'corruption': 'daily_corruption', 'egg_count': 'daily_egg_count',
            'elder_council': 'daily_elder_council', 'feather_fashion': 'daily_feather_fashion',
            'fishing_spot': 'daily_fishing_spot', 'foreign_goods': 'daily_foreign_goods',
            'grandpa_story': 'daily_grandpa_story', 'love_confession': 'daily_love_confession',
            'market_day': 'daily_market_day', 'nest_dispute': 'daily_nest_dispute',
            'night_watch': 'daily_night_watch', 'pond_cleaning': 'daily_pond_cleaning',
            'rain_dance': 'daily_rain_dance', 'royal_inspection': 'daily_royal_inspection',
            'school_debate': 'daily_school_debate', 'talent_show': 'daily_talent_show',
            'tribute_request': 'daily_tribute_request', 'visitor_duck': 'daily_visitor_duck',
            'worm_shortage': 'daily_worm_shortage',
            // Crisis events
            'eagle': 'crisis_eagle', 'earthquake': 'crisis_earthquake',
            'famine': 'crisis_famine', 'fire': 'crisis_fire', 'flood': 'crisis_flood',
            'fox_army': 'crisis_fox_army', 'fox_scout': 'crisis_fox_scout',
            'plague': 'crisis_plague', 'rebellion': 'crisis_rebellion',
            // Opportunity events
            'celebrity': 'opportunity_celebrity', 'golden_egg': 'opportunity_golden_egg',
            'inventor': 'opportunity_inventor', 'merchant': 'opportunity_merchant',
            'peacock': 'opportunity_peacock', 'pirate': 'opportunity_pirate',
            'rainbow': 'opportunity_rainbow', 'swan': 'opportunity_swan',
            'treasure_map': 'opportunity_treasure_map', 'turtle': 'opportunity_turtle',
            // Chain events
            'egg_birth': 'chain_egg_birth', 'egg_cracking': 'chain_egg_cracking', 'egg_moving': 'chain_egg_moving',
            'fox_battle': 'chain_fox_battle', 'fox_final': 'chain_fox_final',
            'fox_negotiate': 'chain_fox_negotiate', 'fox_peace': 'chain_fox_peace', 'fox_war': 'chain_fox_war',
            'sea_calling': 'chain_sea_calling', 'sea_island': 'chain_sea_island', 'sea_storm': 'chain_sea_storm',
            'sky_migration': 'chain_sky_migration', 'sky_proposal': 'chain_sky_proposal', 'sky_training': 'chain_sky_training',
            'true_complete': 'chain_true_complete', 'true_enlighten': 'chain_true_enlighten', 'true_prophecy': 'chain_true_prophecy',
            // Character events
            'sage_appear': 'char_sage_appear', 'sage_advice': 'char_sage_advice',
            'crow_appear': 'char_crow_appear', 'crow_secret': 'char_crow_secret',
            'fox_meeting': 'char_fox_meeting',
            // Milestone events
            'first_building': 'milestone_first_building', 'happiness': 'milestone_happiness',
            'millionaire': 'milestone_millionaire', '50_pop': 'milestone_50_pop',
            '200_pop': 'milestone_200_pop', '500_pop': 'milestone_500_pop', '1000_pop': 'milestone_1000_pop',
            // Seasonal events
            'cherry': 'spring_cherry', 'spring_flood': 'spring_flood', 'love': 'spring_love', 'migration': 'spring_migration',
            'drought': 'summer_drought', 'festival': 'summer_festival', 'heatwave': 'summer_heatwave', 'swimming': 'summer_swimming',
            'goodbye': 'autumn_goodbye', 'harvest': 'autumn_harvest', 'leaves': 'autumn_leaves', 'moon': 'autumn_moon',
            'coldwave': 'winter_coldwave', 'firstsnow': 'winter_firstsnow', 'frozen': 'winter_frozen', 'newyear': 'winter_newyear', 'sick': 'winter_sick'
        };

        // 이벤트 ID에서 키워드 추출 시도
        if (eventId) {
            const cleanId = eventId.split('_').slice(-2).join('_'); // 마지막 2개 부분
            for (const [key, value] of Object.entries(eventImageMap)) {
                if (eventId.toLowerCase().includes(key)) {
                    return `/assets/events/${value}.webp`;
                }
            }
        }

        // 카테고리별 기본 이미지
        const categoryDefaults: Record<string, string> = {
            'daily': 'daily_sunny_day', 'starting': 'daily_sunny_day',
            'seasonal': 'spring_cherry', 'crisis': 'crisis_flood',
            'opportunity': 'opportunity_golden_egg', 'chain': 'chain_fox_battle',
            'character': 'char_sage_appear', 'milestone': 'milestone_first_building',
            'ending': 'milestone_happiness'
        };

        return `/assets/events/${categoryDefaults[category] || 'daily_sunny_day'}.webp`;
    };

    // ============================================
    // RENDER: Main Game
    // ============================================
    return (
        <div
            className="min-h-screen bg-slate-900 text-white flex flex-col font-sans overflow-y-auto transition-all duration-1000 bg-cover bg-center bg-no-repeat bg-fixed relative"
            style={{ backgroundImage: `url(${getEraBackground(resources.era)})` }}
        >
            {/* Overlay for readability */}
            <div className="absolute inset-0 bg-slate-900/35 pointer-events-none z-0"></div>

            {/* 🎉 축하 애니메이션 */}
            {celebration && (
                <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
                    <div className="animate-bounce bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white px-8 py-6 rounded-2xl shadow-2xl border-4 border-yellow-300">
                        <div className="text-6xl text-center mb-2">{celebration.icon}</div>
                        <div className="text-2xl font-bold text-center">{celebration.message}</div>
                    </div>
                </div>
            )}

            {/* 🎉 콤보 카운터 */}
            {showCombo && comboCount > 1 && (
                <div className="fixed top-20 right-4 z-40 animate-pulse">
                    <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-xl shadow-lg">
                        <span className="text-xl font-bold">x{comboCount} COMBO!</span>
                        <span className="text-sm ml-2">+{(comboCount * 5)}%</span>
                    </div>
                </div>
            )}

            {/* Top HUD */}
            <header className="bg-slate-800/90 backdrop-blur border-b border-slate-700 p-2 shadow-lg z-10 relative">
                <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center gap-2">
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-full bg-yellow-500 flex items-center justify-center text-xl shadow-inner border-2 border-yellow-300">
                            {getEraIcon()}
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="font-display text-lg text-yellow-100">{getEraName(resources.era)}</h1>
                                <span className="text-xs text-slate-500">by {playerName}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-400 font-mono">
                                <span>턴 {resources.turn}</span>
                                <span>•</span>
                                <span>{getSeasonIcon()} {getSeasonName(resources.season)} {resources.day}일</span>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-xs font-mono">
                        <div className="flex flex-col items-center bg-slate-700/50 rounded px-2 py-1">
                            <span className="text-slate-300 text-xs font-bold">오링</span>
                            <span className="text-yellow-400 font-bold text-sm">🍩{resources.money.toLocaleString()}</span>
                        </div>
                        <div className="flex flex-col items-center bg-slate-700/50 rounded px-2 py-1">
                            <span className="text-slate-300 text-xs font-bold">클릭</span>
                            <span className="text-amber-400 font-bold text-sm">+{calculateClickValue().value}</span>
                        </div>
                        <div className="flex flex-col items-center bg-slate-700/50 rounded px-2 py-1">
                            <span className="text-slate-300 text-xs font-bold">수입{isOverworked && '⚠️'}</span>
                            <span className={`font-bold text-sm ${isOverworked ? 'text-red-400' : 'text-green-400'}`}>
                                +{calculateIncome()}{calculateAutoMining() > 0 && <span className="text-cyan-400">+{calculateAutoMining()}</span>}
                            </span>
                        </div>
                        <div className="flex flex-col items-center bg-slate-700/50 rounded px-2 py-1">
                            <span className="text-slate-300 text-xs font-bold">인구</span>
                            <span className="text-blue-300 font-bold text-sm">{resources.population}</span>
                        </div>
                        <div className="flex flex-col items-center bg-slate-700/50 rounded px-2 py-1">
                            <span className="text-slate-300 text-xs font-bold">행복</span>
                            <span className={`font-bold text-sm ${resources.happiness < 30 ? 'text-red-500' : 'text-pink-400'}`}>
                                {resources.happiness}%
                            </span>
                        </div>
                    </div>
                    <div className="flex gap-1">
                        {/* 수동 저장 버튼 */}
                        <button
                            onClick={handleManualSave}
                            className={`px-2 py-1 rounded-lg text-xs font-bold transition-colors shrink-0 ${showSaveSuccess ? 'bg-green-600 text-white' : 'bg-blue-600 hover:bg-blue-500 text-white'}`}
                            title="수동 저장"
                        >
                            {showSaveSuccess ? '✓' : '💾'}
                        </button>
                        {/* 도움말 버튼 */}
                        <button
                            onClick={() => setShowHelp(true)}
                            className="bg-purple-600 hover:bg-purple-500 text-white px-2 py-1 rounded-lg text-xs font-bold transition-colors shrink-0"
                            title="게임 설명서"
                        >
                            ❓
                        </button>
                    </div>
                </div>
            </header>

            {/* MBTI + Gimmick Status Panel */}
            <div className="bg-slate-800/80 backdrop-blur border-b border-purple-800/50 px-2 py-2 z-10 relative">
                <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-4">
                    {/* MBTI - 더 눈에 띄게 */}
                    <div className="flex items-center gap-2 bg-purple-900/40 px-2 py-1 rounded-lg border border-purple-500/30">
                        <span className="text-xl">{DUCK_PERSONALITIES[calculateMBTIType(mbti)].icon}</span>
                        <div>
                            <div className="flex items-center gap-1">
                                <span className="text-purple-300 font-bold text-sm sm:text-lg tracking-widest">{getMBTIDisplay(mbti)}</span>
                                <span className="text-purple-400 text-[10px] hidden sm:inline">{DUCK_PERSONALITIES[calculateMBTIType(mbti)].name}</span>
                            </div>
                            {/* MBTI 축별 진행 바 */}
                            <div className="flex gap-1 mt-1">
                                {/* E/I */}
                                <div className="flex items-center gap-0.5" title={`외향(E) ↔ 내향(I): ${mbti.extroversion}`}>
                                    <span className={`text-xs ${mbti.extroversion > 0 ? 'text-yellow-400' : 'text-blue-400'}`}>
                                        {mbti.extroversion > 0 ? 'E' : 'I'}
                                    </span>
                                    <div className="w-8 h-1.5 bg-slate-600 rounded overflow-hidden">
                                        <div
                                            className={`h-full ${mbti.extroversion > 0 ? 'bg-yellow-400' : 'bg-blue-400'}`}
                                            style={{ width: `${Math.min(100, Math.abs(mbti.extroversion))}%` }}
                                        />
                                    </div>
                                </div>
                                {/* S/N */}
                                <div className="flex items-center gap-0.5" title={`감각(S) ↔ 직관(N): ${mbti.sensing}`}>
                                    <span className={`text-xs ${mbti.sensing > 0 ? 'text-green-400' : 'text-pink-400'}`}>
                                        {mbti.sensing > 0 ? 'S' : 'N'}
                                    </span>
                                    <div className="w-8 h-1.5 bg-slate-600 rounded overflow-hidden">
                                        <div
                                            className={`h-full ${mbti.sensing > 0 ? 'bg-green-400' : 'bg-pink-400'}`}
                                            style={{ width: `${Math.min(100, Math.abs(mbti.sensing))}%` }}
                                        />
                                    </div>
                                </div>
                                {/* T/F */}
                                <div className="flex items-center gap-0.5" title={`사고(T) ↔ 감정(F): ${mbti.thinking}`}>
                                    <span className={`text-xs ${mbti.thinking > 0 ? 'text-cyan-400' : 'text-red-400'}`}>
                                        {mbti.thinking > 0 ? 'T' : 'F'}
                                    </span>
                                    <div className="w-8 h-1.5 bg-slate-600 rounded overflow-hidden">
                                        <div
                                            className={`h-full ${mbti.thinking > 0 ? 'bg-cyan-400' : 'bg-red-400'}`}
                                            style={{ width: `${Math.min(100, Math.abs(mbti.thinking))}%` }}
                                        />
                                    </div>
                                </div>
                                {/* J/P */}
                                <div className="flex items-center gap-0.5" title={`판단(J) ↔ 인식(P): ${mbti.judging}`}>
                                    <span className={`text-xs ${mbti.judging > 0 ? 'text-orange-400' : 'text-violet-400'}`}>
                                        {mbti.judging > 0 ? 'J' : 'P'}
                                    </span>
                                    <div className="w-8 h-1.5 bg-slate-600 rounded overflow-hidden">
                                        <div
                                            className={`h-full ${mbti.judging > 0 ? 'bg-orange-400' : 'bg-violet-400'}`}
                                            style={{ width: `${Math.min(100, Math.abs(mbti.judging))}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 기믹 상태 표시 */}
                    <div className="flex flex-wrap justify-center gap-2 text-xs">
                        {/* 세대 갈등 */}
                        <div className="flex items-center gap-1 bg-slate-700/50 px-2 py-1 rounded">
                            <span>👴</span>
                            <div className="w-10 h-2 bg-slate-600 rounded overflow-hidden">
                                <div className={`h-full transition-all ${elderSatisfaction > 50 ? 'bg-green-500' : elderSatisfaction > 25 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                    style={{ width: `${elderSatisfaction}%` }} />
                            </div>
                        </div>
                        <div className="flex items-center gap-1 bg-slate-700/50 px-2 py-1 rounded">
                            <span>🐤</span>
                            <div className="w-10 h-2 bg-slate-600 rounded overflow-hidden">
                                <div className={`h-full transition-all ${youthSatisfaction > 50 ? 'bg-green-500' : youthSatisfaction > 25 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                    style={{ width: `${youthSatisfaction}%` }} />
                            </div>
                        </div>

                        {/* 소문 */}
                        <div className="flex items-center gap-1 bg-slate-700/50 px-2 py-1 rounded">
                            <span>📢</span>
                            <span className="text-green-400">+{goodRumors}</span>
                            <span className="text-red-400">-{badRumors}</span>
                        </div>

                        {/* 과로 */}
                        {isOverworked && (
                            <div className="flex items-center gap-1 bg-red-900/50 px-2 py-1 rounded animate-pulse">
                                <span>😵</span>
                                <span className="text-red-400">{overworkTimer}s</span>
                            </div>
                        )}

                        {/* 다음 시대 조건 */}
                        {(() => {
                            const nextEra = getNextEra(resources.era);
                            if (!nextEra) return null;
                            const req = ERA_REQUIREMENTS[nextEra];
                            if (!req) return null;
                            const popProgress = Math.min(100, (resources.population / req.population) * 100);
                            const moneyProgress = Math.min(100, (resources.money / req.money) * 100);
                            const isReady = resources.population >= req.population && resources.money >= req.money;
                            return (
                                <div className={`flex items-center gap-2 px-2 py-1 rounded border ${isReady ? 'bg-green-900/50 border-green-500/50 animate-pulse' : 'bg-slate-700/50 border-slate-600/50'}`}>
                                    <span className="text-xs text-slate-400">→{getEraName(nextEra)}</span>
                                    <div className="flex items-center gap-1">
                                        <span className="text-xs">👥</span>
                                        <div className="w-10 h-1.5 bg-slate-600 rounded overflow-hidden">
                                            <div className={`h-full ${popProgress >= 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${popProgress}%` }} />
                                        </div>
                                        <span className={`text-xs ${resources.population >= req.population ? 'text-green-400' : 'text-slate-400'}`}>
                                            {resources.population}/{req.population}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <span className="text-xs">🍩</span>
                                        <div className="w-10 h-1.5 bg-slate-600 rounded overflow-hidden">
                                            <div className={`h-full ${moneyProgress >= 100 ? 'bg-green-500' : 'bg-yellow-500'}`} style={{ width: `${moneyProgress}%` }} />
                                        </div>
                                        <span className={`text-xs ${resources.money >= req.money ? 'text-green-400' : 'text-slate-400'}`}>
                                            {resources.money >= req.money ? '✓' : `${Math.floor((resources.money / req.money) * 100)}%`}
                                        </span>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <main className="md:flex-1 relative flex flex-col md:flex-row max-w-7xl mx-auto w-full p-2 gap-2 z-10">
                {/* Left: Upgrade Panel - 데스크톱만 */}
                <aside className="hidden md:block w-48 flex-shrink-0 max-h-[calc(100vh-180px)] overflow-y-scroll">
                    <div className="bg-gradient-to-b from-amber-900/80 to-slate-800/80 backdrop-blur rounded-xl border border-amber-700/50 p-2">
                        <div className="flex items-center justify-between mb-2 pb-1 border-b border-amber-700/30">
                            <span className="text-amber-400 font-bold text-sm">⛏️ 채굴 도구</span>
                            <span className="text-xs text-amber-300">+{calculateClickValue().value}/클릭</span>
                        </div>

                        {/* 자동 채굴 표시 */}
                        {calculateAutoMining() > 0 && (
                            <div className="mb-2 px-2 py-1 bg-green-900/30 rounded text-xs text-green-400 text-center">
                                🤖 자동: +{calculateAutoMining()}/초
                            </div>
                        )}

                        <div className="space-y-1.5">
                            {(Object.values(miningUpgrades) as MiningUpgrade[]).map(upgrade => {
                                const cost = getUpgradeCost(upgrade.id);
                                const canAfford = resources.money >= cost;
                                const isMaxed = upgrade.level >= upgrade.maxLevel;

                                // 현재 효과 계산
                                const currentEffect = [];
                                if (upgrade.effect.clickBonus) {
                                    currentEffect.push(`+${upgrade.effect.clickBonus * upgrade.level}/클릭`);
                                }
                                if (upgrade.effect.clickMultiplier && upgrade.level > 0) {
                                    currentEffect.push(`x${Math.pow(upgrade.effect.clickMultiplier, upgrade.level).toFixed(1)}`);
                                }
                                if (upgrade.effect.autoMining) {
                                    currentEffect.push(`+${upgrade.effect.autoMining * upgrade.level}/초`);
                                }
                                if (upgrade.effect.luckyChance) {
                                    currentEffect.push(`🍀${Math.floor(upgrade.effect.luckyChance * upgrade.level * 100)}%`);
                                }

                                return (
                                    <button
                                        key={upgrade.id}
                                        onClick={() => handleUpgradePurchase(upgrade.id)}
                                        disabled={!canAfford || isMaxed}
                                        className={`w-full p-1.5 rounded-lg text-left transition-all ${isMaxed
                                                ? 'bg-amber-900/50 border border-amber-600 opacity-60'
                                                : canAfford
                                                    ? 'bg-slate-700 hover:bg-amber-800/50 border border-slate-600 hover:border-amber-500'
                                                    : 'bg-slate-800 border border-slate-700 opacity-50'
                                            }`}
                                    >
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-lg">{upgrade.icon}</span>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs font-bold text-slate-200 truncate">{upgrade.name}</span>
                                                    <span className={`text-xs ${isMaxed ? 'text-amber-400' : 'text-slate-400'}`}>
                                                        Lv.{upgrade.level}{isMaxed && ' MAX'}
                                                    </span>
                                                </div>
                                                {/* 현재 효과 표시 */}
                                                {upgrade.level > 0 && (
                                                    <div className="text-xs text-cyan-400 font-mono">
                                                        {currentEffect.join(' ')}
                                                    </div>
                                                )}
                                                {!isMaxed && (
                                                    <div className={`text-xs ${canAfford ? 'text-yellow-400' : 'text-slate-500'}`}>
                                                        🍩 {cost.toLocaleString()}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {/* 채굴 속도 경고 */}
                        {miningHistory.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-amber-700/30">
                                <div className="text-xs text-slate-400">
                                    최근 3분 채굴: {miningHistory.reduce((sum, h) => sum + h.amount, 0).toLocaleString()}
                                    {miningHistory.reduce((sum, h) => sum + h.amount, 0) > MINIGAME_TRIGGER_THRESHOLD * 0.7 && (
                                        <span className="text-amber-400 animate-pulse ml-1">⚠️</span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </aside>

                {/* Center: Click Area */}
                <section className="flex-1 flex flex-col gap-2 min-h-0 items-center">
                    <div
                        className={`w-[85vw] max-w-[420px] sm:w-[60vw] sm:max-w-[480px] md:w-[50vw] md:max-w-[520px] aspect-square rounded-2xl border-2 relative overflow-hidden group transition-all shadow-2xl select-none cursor-pointer ${isOverworked
                            ? 'border-red-500 hover:border-red-400'
                            : 'border-yellow-500/50 hover:border-yellow-400'
                            }`}
                        ref={clickAreaRef}
                        onClick={handleManualGather}
                        style={{ backgroundImage: `url(${getEraBackground(resources.era)})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                    >
                        {/* 반투명 오버레이 - 텍스트 가독성 확보 */}
                        <div className={`absolute inset-0 ${isOverworked ? 'bg-red-900/40' : 'bg-slate-900/30'}`}></div>
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_transparent_30%,_rgba(0,0,0,0.4)_100%)]"></div>
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 border-2 border-yellow-400/20 rounded-full animate-[ping_3s_linear_infinite]"></div>

                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className={`relative transition-transform duration-100 ${isClicking ? 'scale-90' : 'scale-100'}`}>
                                <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-xl animate-bounce drop-shadow-lg">{getDuckMood()}</div>
                                <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-3xl z-20 transform -rotate-6 drop-shadow-lg">{getDuckOutfit()}</div>
                                <div className={`text-[80px] md:text-[100px] leading-none filter drop-shadow-[0_8px_16px_rgba(0,0,0,0.8)] ${isOverworked ? 'grayscale' : ''}`}
                                    style={{ textShadow: '0 0 30px rgba(255,255,255,0.3), 0 0 60px rgba(255,200,0,0.2)' }}>
                                    {getDuckFace()}
                                </div>
                            </div>
                        </div>

                        <div className="absolute bottom-2 left-2 right-2 flex justify-between items-end">
                            <div className="text-xs text-slate-200 bg-slate-900/90 backdrop-blur px-3 py-1.5 rounded-lg max-w-[55%] truncate shadow-lg border border-slate-700/50">{TIPS[currentTip]}</div>
                            <button onClick={(e) => { e.stopPropagation(); triggerRandomEvent(); }} className="bg-purple-600 hover:bg-purple-500 text-white text-xs px-4 py-2 rounded-full font-bold shadow-lg hover:shadow-purple-500/30 transition-all">
                                🔮 이벤트
                            </button>
                        </div>

                        {activeChains.length > 0 && (
                            <div className="absolute top-2 left-2 bg-purple-900/80 px-2 py-1 rounded-full text-xs">
                                📖 {activeChains[0].name}
                            </div>
                        )}
                    </div>

                    {/* Log Panel */}
                    <div className="w-[90vw] max-w-[480px] sm:w-[70vw] sm:max-w-[560px] md:w-[55vw] md:max-w-[600px] h-64 bg-slate-900/80 backdrop-blur rounded-xl border-2 border-purple-900/50 p-2 overflow-y-auto font-mono text-xs shadow-[inset_0_2px_20px_rgba(139,92,246,0.1)]">
                        <div className="flex items-center gap-2 mb-2 pb-1 border-b border-purple-800/30">
                            <span className="text-purple-400 animate-pulse">●</span>
                            <span className="text-purple-300 font-bold tracking-wider">왕국 기록</span>
                        </div>
                        <div className="flex flex-col gap-1">
                            {logs.length === 0 && (
                                <span className="text-slate-500 italic text-center py-4">✨ 오리를 클릭하여 모험을 시작하세요...</span>
                            )}
                            {logs.map(log => (
                                <div key={log.id} className={`flex items-start gap-1.5 px-1.5 py-0.5 rounded transition-all
                                    ${log.type === 'info' ? 'text-slate-300 bg-slate-800/30' : ''}
                                    ${log.type === 'success' ? 'text-green-400 bg-green-900/20 border-l-2 border-green-500' : ''}
                                    ${log.type === 'warning' ? 'text-yellow-400 bg-yellow-900/20 border-l-2 border-yellow-500' : ''}
                                    ${log.type === 'danger' ? 'text-red-400 bg-red-900/20 border-l-2 border-red-500' : ''}
                                    ${log.type === 'special' ? 'text-purple-300 bg-purple-900/30 border-l-2 border-purple-400' : ''}
                                `}>
                                    <span className="opacity-70 shrink-0">{log.icon || (log.type === 'success' ? '✓' : log.type === 'warning' ? '⚠' : log.type === 'danger' ? '✗' : log.type === 'special' ? '★' : '•')}</span>
                                    <span>{log.message}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Right: Buildings - 데스크톱만 */}
                <aside className="hidden md:block md:w-72 flex-shrink-0">
                    <BuildingPanel buildings={buildings} onPurchase={handlePurchase} currentMoney={resources.money} currentEra={resources.era} />
                </aside>
            </main>

            {/* 모바일 하단 버튼 */}
            <div className="md:hidden fixed bottom-4 left-4 right-4 flex gap-2 z-40">
                <button
                    onClick={() => setShowMiningPopup(true)}
                    className="flex-1 bg-amber-700 hover:bg-amber-600 text-white py-3 px-4 rounded-xl font-bold shadow-lg border-2 border-amber-500"
                >
                    ⛏️ 채굴 도구
                </button>
                <button
                    onClick={() => setShowBuildingPopup(true)}
                    className="flex-1 bg-blue-700 hover:bg-blue-600 text-white py-3 px-4 rounded-xl font-bold shadow-lg border-2 border-blue-500"
                >
                    🏗️ 건설
                </button>
            </div>

            {/* 채굴 도구 팝업 */}
            {showMiningPopup && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end md:hidden" onClick={() => setShowMiningPopup(false)}>
                    <div className="w-full max-h-[70vh] bg-gradient-to-b from-amber-900 to-slate-800 rounded-t-2xl border-t-2 border-amber-500 overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="sticky top-0 bg-amber-900 p-3 border-b border-amber-700 flex justify-between items-center">
                            <span className="text-amber-400 font-bold">⛏️ 채굴 도구 (+{calculateClickValue().value}/클릭)</span>
                            <button onClick={() => setShowMiningPopup(false)} className="text-white text-2xl">✕</button>
                        </div>
                        <div className="p-3 overflow-y-auto max-h-[calc(70vh-60px)]">
                            {calculateAutoMining() > 0 && (
                                <div className="mb-3 px-3 py-2 bg-green-900/50 rounded-lg text-green-400 text-center">
                                    🤖 자동 채굴: +{calculateAutoMining()}/초
                                </div>
                            )}
                            <div className="space-y-2">
                                {(Object.values(miningUpgrades) as MiningUpgrade[]).map(upgrade => {
                                    const cost = getUpgradeCost(upgrade.id);
                                    const canAfford = resources.money >= cost;
                                    const isMaxed = upgrade.level >= upgrade.maxLevel;
                                    const currentEffect = [];
                                    if (upgrade.effect.clickBonus) currentEffect.push(`+${upgrade.effect.clickBonus * upgrade.level}/클릭`);
                                    if (upgrade.effect.clickMultiplier && upgrade.level > 0) currentEffect.push(`x${Math.pow(upgrade.effect.clickMultiplier, upgrade.level).toFixed(1)}`);
                                    if (upgrade.effect.autoMining) currentEffect.push(`+${upgrade.effect.autoMining * upgrade.level}/초`);
                                    if (upgrade.effect.luckyChance) currentEffect.push(`🍀${Math.floor(upgrade.effect.luckyChance * upgrade.level * 100)}%`);

                                    return (
                                        <button
                                            key={upgrade.id}
                                            onClick={() => handleUpgradePurchase(upgrade.id)}
                                            disabled={!canAfford || isMaxed}
                                            className={`w-full p-3 rounded-xl text-left transition-all ${isMaxed
                                                ? 'bg-amber-900/50 border-2 border-amber-600'
                                                : canAfford
                                                    ? 'bg-slate-700 hover:bg-amber-800/50 border-2 border-slate-600 hover:border-amber-500'
                                                    : 'bg-slate-800 border-2 border-slate-700 opacity-50'
                                            }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className="text-2xl">{upgrade.icon}</span>
                                                <div className="flex-1">
                                                    <div className="flex justify-between items-center">
                                                        <span className="font-bold text-white">{upgrade.name}</span>
                                                        <span className={isMaxed ? 'text-amber-400' : 'text-slate-400'}>
                                                            Lv.{upgrade.level}{isMaxed && ' MAX'}
                                                        </span>
                                                    </div>
                                                    {upgrade.level > 0 && <div className="text-sm text-cyan-400">{currentEffect.join(' ')}</div>}
                                                    {!isMaxed && <div className={`text-sm ${canAfford ? 'text-yellow-400' : 'text-slate-500'}`}>🍩 {cost.toLocaleString()}</div>}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 건설 팝업 */}
            {showBuildingPopup && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end md:hidden" onClick={() => setShowBuildingPopup(false)}>
                    <div className="w-full max-h-[70vh] bg-slate-800 rounded-t-2xl border-t-2 border-blue-500 overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="sticky top-0 bg-slate-700 p-3 border-b border-slate-600 flex justify-between items-center">
                            <span className="text-yellow-400 font-bold">🏗️ 건설</span>
                            <button onClick={() => setShowBuildingPopup(false)} className="text-white text-2xl">✕</button>
                        </div>
                        <div className="overflow-y-auto max-h-[calc(70vh-60px)]">
                            <BuildingPanel buildings={buildings} onPurchase={handlePurchase} currentMoney={resources.money} currentEra={resources.era} />
                        </div>
                    </div>
                </div>
            )}

            <EventModal event={currentEvent} onSelectOption={handleEventOption} illustration={currentEvent ? getEventIllustration(currentEvent.category, currentEvent.id) : undefined} />

            {/* 🎮 미니게임 모달 */}
            {activeMiniGame && miniGameData && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl p-6 max-w-md w-full border-2 border-purple-500 shadow-2xl animate-fadeIn">
                        {/* 헤더 */}
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-purple-400">
                                {activeMiniGame === 'hidden_picture' ? '🔍 숨은 그림 찾기' : '🧩 퍼즐 맞추기'}
                            </h2>
                            <div className="flex items-center gap-2">
                                <span className={`text-lg font-bold ${miniGameTimer <= 10 ? 'text-red-400 animate-pulse' : 'text-yellow-400'}`}>
                                    ⏱️ {miniGameTimer}초
                                </span>
                            </div>
                        </div>

                        {/* 숨은그림찾기 게임 */}
                        {activeMiniGame === 'hidden_picture' && (
                            <div className="space-y-4">
                                <div className="text-center">
                                    <span className="text-2xl">{(miniGameData as HiddenPictureGame).targetEmoji}</span>
                                    <span className="text-slate-300 ml-2">
                                        를 찾으세요! ({(miniGameData as HiddenPictureGame).foundCount}/{(miniGameData as HiddenPictureGame).targetCount})
                                    </span>
                                </div>

                                <div className="grid gap-1 bg-slate-700 p-3 rounded-xl" style={{
                                    gridTemplateColumns: `repeat(${(miniGameData as HiddenPictureGame).gridSize}, 1fr)`
                                }}>
                                    {(miniGameData as HiddenPictureGame).grid.map((row, y) =>
                                        row.map((emoji, x) => {
                                            const isTarget = (miniGameData as HiddenPictureGame).targetPositions.some(p => p.x === x && p.y === y);
                                            const isFound = !isTarget || (miniGameData as HiddenPictureGame).foundCount >= (miniGameData as HiddenPictureGame).targetCount;

                                            return (
                                                <button
                                                    key={`${x}-${y}`}
                                                    onClick={() => {
                                                        if (isTarget && (miniGameData as HiddenPictureGame).grid[y][x] === (miniGameData as HiddenPictureGame).targetEmoji) {
                                                            const newData = { ...(miniGameData as HiddenPictureGame) };
                                                            newData.foundCount++;
                                                            newData.grid[y][x] = '✅';
                                                            setMiniGameData(newData);
                                                            setMiniGameScore(prev => prev + 1);

                                                            if (newData.foundCount >= newData.targetCount) {
                                                                setTimeout(() => completeMiniGame(true), 500);
                                                            }
                                                        }
                                                    }}
                                                    className="aspect-square flex items-center justify-center text-2xl bg-slate-600 hover:bg-slate-500 rounded transition-all hover:scale-110"
                                                >
                                                    {emoji}
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        )}

                        {/* 퍼즐 게임 */}
                        {activeMiniGame === 'puzzle' && (
                            <div className="space-y-4">
                                <div className="text-center text-slate-300">
                                    숫자를 1-8 순서로 정렬하세요!
                                    <span className="ml-2 text-yellow-400">이동: {(miniGameData as PuzzleGame).moves}</span>
                                </div>

                                <div className="grid grid-cols-3 gap-2 bg-slate-700 p-4 rounded-xl">
                                    {(miniGameData as PuzzleGame).pieces.map((piece, idx) => {
                                        const isEmpty = piece === 8;

                                        return (
                                            <button
                                                key={idx}
                                                onClick={() => {
                                                    const data = miniGameData as PuzzleGame;
                                                    const emptyIdx = data.pieces.indexOf(8);
                                                    const row = Math.floor(idx / 3);
                                                    const col = idx % 3;
                                                    const emptyRow = Math.floor(emptyIdx / 3);
                                                    const emptyCol = emptyIdx % 3;

                                                    // 인접한 칸만 이동 가능
                                                    if ((Math.abs(row - emptyRow) === 1 && col === emptyCol) ||
                                                        (Math.abs(col - emptyCol) === 1 && row === emptyRow)) {
                                                        const newPieces = [...data.pieces];
                                                        [newPieces[idx], newPieces[emptyIdx]] = [newPieces[emptyIdx], newPieces[idx]];

                                                        const newData = { ...data, pieces: newPieces, moves: data.moves + 1 };
                                                        setMiniGameData(newData);

                                                        // 완료 체크
                                                        const isComplete = newPieces.every((p, i) => p === i);
                                                        if (isComplete) {
                                                            setMiniGameScore(Math.max(1, 10 - Math.floor(newData.moves / 5)));
                                                            setTimeout(() => completeMiniGame(true), 500);
                                                        }
                                                    }
                                                }}
                                                disabled={isEmpty}
                                                className={`aspect-square flex items-center justify-center text-3xl font-bold rounded-lg transition-all ${isEmpty
                                                        ? 'bg-slate-800'
                                                        : 'bg-gradient-to-br from-purple-600 to-purple-800 hover:from-purple-500 hover:to-purple-700 text-white shadow-lg hover:scale-105'
                                                    }`}
                                            >
                                                {!isEmpty && piece + 1}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* 건너뛰기 버튼 */}
                        <button
                            onClick={() => completeMiniGame(false)}
                            className="w-full mt-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors"
                        >
                            건너뛰기 (위로금만 받기)
                        </button>
                    </div>
                </div>
            )}

            {/* 도움말 모달 */}
            {showHelp && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowHelp(false)}>
                    <div className="w-full max-w-2xl max-h-[85vh] bg-slate-800 rounded-2xl border-2 border-purple-500 overflow-hidden" onClick={e => e.stopPropagation()}>
                        {/* 헤더 */}
                        <div className="bg-purple-900 p-4 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-purple-300">📖 오링왕국 설명서</h2>
                            <button onClick={() => setShowHelp(false)} className="text-white text-2xl hover:text-purple-300">✕</button>
                        </div>

                        {/* 탭 */}
                        <div className="flex border-b border-slate-700">
                            {[
                                { id: 'basic', label: '🎮 기본' },
                                { id: 'ui', label: '📊 UI 설명' },
                                { id: 'endings', label: '🏆 엔딩 조건' },
                                { id: 'buildings', label: '🏗️ 건물' }
                            ].map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setHelpTab(tab.id as typeof helpTab)}
                                    className={`flex-1 py-2 text-sm font-bold transition-colors ${
                                        helpTab === tab.id
                                            ? 'bg-purple-600 text-white'
                                            : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* 내용 */}
                        <div className="p-4 overflow-y-auto max-h-[calc(85vh-120px)]">
                            {/* 기본 설명 */}
                            {helpTab === 'basic' && (
                                <div className="space-y-4 text-sm">
                                    <div className="bg-slate-700 rounded-lg p-3">
                                        <h3 className="text-yellow-400 font-bold mb-2">🎯 게임 목표</h3>
                                        <p className="text-slate-300">오리를 클릭해서 오링(화폐)을 모으고, 건물을 짓고, 이벤트를 해결하며 왕국을 발전시키세요. 다양한 조건을 만족하면 18가지 엔딩 중 하나를 볼 수 있습니다!</p>
                                    </div>

                                    <div className="bg-slate-700 rounded-lg p-3">
                                        <h3 className="text-yellow-400 font-bold mb-2">⏱️ 게임 진행</h3>
                                        <ul className="text-slate-300 space-y-1">
                                            <li>• <span className="text-cyan-400">1초 = 1턴</span>: 매 초마다 건물 수입이 들어옵니다</li>
                                            <li>• <span className="text-cyan-400">30턴 = 1계절</span>: 봄→여름→가을→겨울 순환</li>
                                            <li>• <span className="text-cyan-400">이벤트</span>: 랜덤으로 발생, 선택에 따라 결과 변화</li>
                                            <li>• <span className="text-cyan-400">MBTI</span>: 선택에 따라 성격이 결정됩니다</li>
                                        </ul>
                                    </div>

                                    <div className="bg-slate-700 rounded-lg p-3">
                                        <h3 className="text-yellow-400 font-bold mb-2">⚡ 기믹 시스템</h3>
                                        <ul className="text-slate-300 space-y-1">
                                            <li>• <span className="text-red-400">클릭 과로</span>: 3초 내 너무 많이 클릭하면 피로!</li>
                                            <li>• <span className="text-orange-400">연속 선택</span>: 같은 유형 선택 반복 시 반발</li>
                                            <li>• <span className="text-blue-400">세대 갈등</span>: 원로/젊은이 균형 필요</li>
                                            <li>• <span className="text-green-400">소문</span>: 좋은/나쁜 소문이 퍼집니다</li>
                                        </ul>
                                    </div>

                                    <div className="bg-slate-700 rounded-lg p-3">
                                        <h3 className="text-yellow-400 font-bold mb-2">💾 저장 기능</h3>
                                        <p className="text-slate-300">게임은 30초마다 자동 저장됩니다. 같은 닉네임으로 다시 시작하면 이어서 플레이할 수 있습니다!</p>
                                    </div>
                                </div>
                            )}

                            {/* UI 설명 */}
                            {helpTab === 'ui' && (
                                <div className="space-y-3 text-sm">
                                    <div className="bg-slate-700 rounded-lg p-3">
                                        <h3 className="text-yellow-400 font-bold mb-2">📊 상단 HUD</h3>
                                        <div className="grid grid-cols-2 gap-2 text-slate-300">
                                            <div className="flex items-center gap-2">
                                                <span className="text-yellow-400">🍩</span>
                                                <span><strong>오링</strong>: 화폐 (돈)</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-green-400">+숫자</span>
                                                <span><strong>수입</strong>: 초당 자동 획득량</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-amber-400">+숫자</span>
                                                <span><strong>클릭</strong>: 클릭당 획득량</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-blue-300">숫자</span>
                                                <span><strong>인구</strong>: 현재 오리 수</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-pink-400">%</span>
                                                <span><strong>행복</strong>: 백성 행복도</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span>턴 숫자</span>
                                                <span><strong>턴</strong>: 경과 시간</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-slate-700 rounded-lg p-3">
                                        <h3 className="text-yellow-400 font-bold mb-2">🧠 MBTI 패널</h3>
                                        <div className="text-slate-300 space-y-1">
                                            <p>선택에 따라 성격이 결정됩니다. 각 축의 색상:</p>
                                            <div className="grid grid-cols-2 gap-1 mt-2">
                                                <span><span className="text-yellow-400">E</span>(외향) / <span className="text-blue-400">I</span>(내향)</span>
                                                <span><span className="text-green-400">S</span>(감각) / <span className="text-pink-400">N</span>(직관)</span>
                                                <span><span className="text-cyan-400">T</span>(사고) / <span className="text-red-400">F</span>(감정)</span>
                                                <span><span className="text-orange-400">J</span>(판단) / <span className="text-violet-400">P</span>(인식)</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-slate-700 rounded-lg p-3">
                                        <h3 className="text-yellow-400 font-bold mb-2">📈 기믹 상태</h3>
                                        <div className="text-slate-300 space-y-1">
                                            <div className="flex items-center gap-2">
                                                <span>👴</span>
                                                <span><strong>원로 만족도</strong>: 전통적 선택 시 상승</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span>🐤</span>
                                                <span><strong>젊은이 만족도</strong>: 혁신적 선택 시 상승</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span>📢</span>
                                                <span><strong>소문</strong>: +좋은소문 / -나쁜소문</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span>😵</span>
                                                <span><strong>과로</strong>: 과로 시 효율 50% 감소</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-slate-700 rounded-lg p-3">
                                        <h3 className="text-yellow-400 font-bold mb-2">⬆️ 다음 시대 조건</h3>
                                        <p className="text-slate-300 mb-2">우측에 표시되는 진행 바:</p>
                                        <div className="text-slate-300 space-y-1">
                                            <div className="flex items-center gap-2">
                                                <span>👥</span>
                                                <span><strong>인구 진행률</strong>: 필요 인구 달성률</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span>🍩</span>
                                                <span><strong>오링 진행률</strong>: 필요 오링 달성률</span>
                                            </div>
                                        </div>
                                        <div className="mt-2 text-xs text-slate-400">
                                            연못→마을: 인구50 + 1천 | 마을→도시: 인구200 + 1만<br/>
                                            도시→왕국: 인구500 + 10만 | 왕국→제국: 인구1000 + 100만
                                        </div>
                                    </div>

                                    <div className="bg-slate-700 rounded-lg p-3">
                                        <h3 className="text-yellow-400 font-bold mb-2">🏗️ 건물 패널 아이콘</h3>
                                        <div className="text-slate-300 space-y-1">
                                            <div className="flex items-center gap-2">
                                                <span>🛡️</span>
                                                <span><strong>방어력</strong>: 위기 이벤트 피해 감소</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span>👥+숫자</span>
                                                <span><strong>인구 보너스</strong>: 건설 시 인구 증가</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span>😊+숫자</span>
                                                <span><strong>행복 보너스</strong>: 건설 시 행복도 증가</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span>+숫자/초</span>
                                                <span><strong>수입</strong>: 초당 자동 획득량</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* 엔딩 조건 */}
                            {helpTab === 'endings' && (
                                <div className="space-y-3 text-sm">
                                    <div className="bg-green-900/30 border border-green-500/50 rounded-lg p-3">
                                        <h3 className="text-green-400 font-bold mb-2">✨ 긍정적 엔딩</h3>
                                        <div className="space-y-2 text-slate-300">
                                            <div className="border-b border-slate-600 pb-2">
                                                <span className="text-yellow-400">👑 대륙의 지배자</span>
                                                <p className="text-xs">인구 1,000명 이상 + 오링 500만 이상</p>
                                            </div>
                                            <div className="border-b border-slate-600 pb-2">
                                                <span className="text-yellow-400">☮️ 영원한 평화</span>
                                                <p className="text-xs">행복도 85% 이상 유지하며 200턴 이상 플레이</p>
                                            </div>
                                            <div className="border-b border-slate-600 pb-2">
                                                <span className="text-yellow-400">🦆✨ 황금 오리의 전설</span>
                                                <p className="text-xs">모든 건물을 최대 개수까지 건설</p>
                                            </div>
                                            <div className="border-b border-slate-600 pb-2">
                                                <span className="text-yellow-400">☁️ 구름 위의 왕국</span>
                                                <p className="text-xs">"하늘로의 여정" 스토리 체인 완료</p>
                                            </div>
                                            <div className="border-b border-slate-600 pb-2">
                                                <span className="text-yellow-400">🌊 심해의 제왕</span>
                                                <p className="text-xs">"심해 탐험" 스토리 체인 완료</p>
                                            </div>
                                            <div className="border-b border-slate-600 pb-2">
                                                <span className="text-yellow-400">📚 전설의 오리</span>
                                                <p className="text-xs">5개 이상의 스토리 완료 + 1000턴 이상 플레이</p>
                                            </div>
                                            <div>
                                                <span className="text-yellow-400">💰 상인 제국</span>
                                                <p className="text-xs">시장 15개(MAX) + 항구 5개(MAX) + 오링 5,000만</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-blue-900/30 border border-blue-500/50 rounded-lg p-3">
                                        <h3 className="text-blue-400 font-bold mb-2">🔄 중립적 엔딩</h3>
                                        <div className="space-y-2 text-slate-300">
                                            <div className="border-b border-slate-600 pb-2">
                                                <span className="text-yellow-400">🦆➡️🏝️ 대이주</span>
                                                <p className="text-xs">이주 이벤트에서 떠나기 선택</p>
                                            </div>
                                            <div className="border-b border-slate-600 pb-2">
                                                <span className="text-yellow-400">🦊🤝🦆 여우와의 동맹</span>
                                                <p className="text-xs">"여우 침공" 스토리에서 외교 루트 선택</p>
                                            </div>
                                            <div>
                                                <span className="text-yellow-400">✨🌟 승천</span>
                                                <p className="text-xs">백조 캐릭터 만남 + 행복도 80% 이상</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-3">
                                        <h3 className="text-red-400 font-bold mb-2">💀 부정적 엔딩 (GAME OVER)</h3>
                                        <div className="space-y-2 text-slate-300">
                                            <div className="border-b border-slate-600 pb-2">
                                                <span className="text-red-400">💀🦆 멸종</span>
                                                <p className="text-xs">인구가 0이 되면 발생</p>
                                            </div>
                                            <div className="border-b border-slate-600 pb-2">
                                                <span className="text-red-400">😠🔥 반란</span>
                                                <p className="text-xs">행복도가 0이 되면 발생</p>
                                            </div>
                                            <div className="border-b border-slate-600 pb-2">
                                                <span className="text-red-400">🦊👑 여우의 정복</span>
                                                <p className="text-xs">"여우 침공" 스토리에서 항복 선택</p>
                                            </div>
                                            <div className="border-b border-slate-600 pb-2">
                                                <span className="text-red-400">🍂💀 대기근</span>
                                                <p className="text-xs">기근 이벤트 대처 실패</p>
                                            </div>
                                            <div>
                                                <span className="text-red-400">🦠☠️ 역병</span>
                                                <p className="text-xs">전염병 이벤트 대처 실패</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-purple-900/30 border border-purple-500/50 rounded-lg p-3">
                                        <h3 className="text-purple-400 font-bold mb-2">🔮 숨겨진 엔딩</h3>
                                        {resources.turn >= 5000 ? (
                                            <div className="space-y-2 text-slate-300">
                                                <div className="border-b border-slate-600 pb-2">
                                                    <span className="text-purple-400">🔮💫 진실의 끝</span>
                                                    <p className="text-xs">캐릭터 5명 이상 만남 + 스토리 3개 이상 완료 + 깨달음</p>
                                                </div>
                                                <div className="border-b border-slate-600 pb-2">
                                                    <span className="text-purple-400">🦆🌟 비밀의 오리</span>
                                                    <p className="text-xs">황금 오리 전설 + "비밀 오리" 스토리 완료</p>
                                                </div>
                                                <div>
                                                    <span className="text-purple-400">🔄⏰ 시간의 고리</span>
                                                    <p className="text-xs">시간 역설 플래그 트리거 (비밀)</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-center py-4">
                                                <span className="text-4xl mb-2 block">🔒</span>
                                                <p className="text-slate-400 text-xs">5000턴 이상 플레이하면 공개됩니다</p>
                                                <p className="text-slate-500 text-xs mt-1">현재: {resources.turn}턴</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* 건물 설명 */}
                            {helpTab === 'buildings' && (
                                <div className="space-y-3 text-sm">
                                    <div className="bg-slate-700 rounded-lg p-3">
                                        <h3 className="text-cyan-400 font-bold mb-2">🌊 연못 시대 (시작)</h3>
                                        <div className="space-y-1 text-slate-300">
                                            <div className="flex justify-between">
                                                <span>🏕️ 지푸라기 둥지</span>
                                                <span className="text-xs">수입+1 | 인구+2 | MAX 30</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>🌾 지렁이 농장</span>
                                                <span className="text-xs">수입+5 | 행복+2 | MAX 25</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-slate-700 rounded-lg p-3">
                                        <h3 className="text-green-400 font-bold mb-2">🏘️ 마을 시대 (인구50 + 1천)</h3>
                                        <div className="space-y-1 text-slate-300">
                                            <div className="flex justify-between">
                                                <span>💎 반짝이는 연못</span>
                                                <span className="text-xs">수입+20 | MAX 15</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>⚔️ 거위 경비대</span>
                                                <span className="text-xs">🛡️방어+10 | MAX 10</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>📚 꽥꽥 학교</span>
                                                <span className="text-xs">수입+2 | 행복+3 | MAX 10</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-slate-700 rounded-lg p-3">
                                        <h3 className="text-blue-400 font-bold mb-2">🏙️ 도시 시대 (인구200 + 1만)</h3>
                                        <div className="space-y-1 text-slate-300">
                                            <div className="flex justify-between">
                                                <span>🏥 치유의 둥지</span>
                                                <span className="text-xs">행복+5 | 전염병 저항 | MAX 10</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>🏪 오링 시장</span>
                                                <span className="text-xs">수입+50 | 행복+5 | MAX 15</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-slate-700 rounded-lg p-3">
                                        <h3 className="text-yellow-400 font-bold mb-2">🏰 왕국 시대 (인구500 + 10만)</h3>
                                        <div className="space-y-1 text-slate-300">
                                            <div className="flex justify-between">
                                                <span>🏰 황금 오리 궁전</span>
                                                <span className="text-xs">수입+100 | 인구+10 | 🛡️+20 | MAX 3</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>⚓ 오링 항구</span>
                                                <span className="text-xs">수입+80 | 인구+5 | MAX 5</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>⛪ 황금알 신전</span>
                                                <span className="text-xs">수입+200 | 신비 이벤트 | MAX 2</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-slate-700 rounded-lg p-3">
                                        <h3 className="text-purple-400 font-bold mb-2">👑 제국 시대 (인구1000 + 100만)</h3>
                                        <div className="space-y-1 text-slate-300">
                                            <div className="flex justify-between">
                                                <span>🗼 세계의 불가사의</span>
                                                <span className="text-xs">수입+1000 | 인구+50 | 🛡️+50 | MAX 1</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-amber-900/30 border border-amber-500/50 rounded-lg p-3">
                                        <h3 className="text-amber-400 font-bold mb-2">⛏️ 채굴 업그레이드</h3>
                                        <div className="space-y-1 text-slate-300 text-xs">
                                            <p>채굴 도구 패널에서 업그레이드 구매 가능:</p>
                                            <div className="mt-2 space-y-1">
                                                <div>• <strong>황금 부리</strong>: 클릭당 +1</div>
                                                <div>• <strong>잠수 고글</strong>: 클릭당 +2</div>
                                                <div>• <strong>보물 지도</strong>: 클릭당 +5</div>
                                                <div>• <strong>마법 지팡이</strong>: 클릭 x2 배수</div>
                                                <div>• <strong>자동 낚시꾼</strong>: 초당 자동 채굴</div>
                                                <div>• <strong>행운의 클로버</strong>: 10% 확률 2배</div>
                                                <div>• <strong>오리 군단</strong>: 오토 클릭 부대</div>
                                                <div>• <strong>황금알</strong>: 궁극의 능력</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default App;
